/**
 * AgUiChatPanel — Chat panel for AG-UI agents (via CopilotKit).
 *
 * This file is a copy of ChatPanel.tsx with only the data-source seams
 * replaced. Everything else (layout, state, refs, styling, resize logic,
 * skill toolbar, pending queue, upload UI, KB scope chips, history nav,
 * suggested questions) is intentionally byte-identical to ChatPanel.tsx so
 * both surfaces stay visually and behaviorally aligned.
 *
 * What's replaced (search for "AG-UI SEAM" markers below):
 *   1. Send path: fetch POST /events + SSE → agent.addMessage + copilotkit.runAgent
 *   2. Messages source: local `messages` state driven by SSE → mirror agent.messages
 *   3. Streaming state: streamingText driven by RAF flush → derived from agent
 *   4. Stop path: abort + POST /stop → copilotkit.stopAgent
 *   5. Resume/history: hidden for AG-UI (CopilotKit handles session on its own)
 *
 * The public component is default-exported and wraps itself in
 * <CopilotKitProvider> so callers don't need to.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  CopilotChatMessageView,
  useAgent,
  useCopilotKit,
  useDefaultRenderTool,
  useHumanInTheLoop,
  useRenderTool,
  useRenderToolCall,
} from '@copilotkit/react-core/v2';
import { ToolCallStatus } from '@copilotkit/core';
import type { ToolCall, ToolMessage } from '@ag-ui/core';
import { useAuth } from '../../../context/AuthContext';
import { useImpersonation } from '../../../context/ImpersonationContext';
import { useUserProfile } from '../../../context/UserProfileContext';
import { useReportMetadata } from '../../../context/ReportMetadataContext';
import { useWorkspaceNav } from '../../../context/WorkspaceNavContext';
import { useChatPanel } from '../../../context/ChatPanelContext';
import { getAgentWelcome } from '../../../context/AgentContext';
import { getDefaultSuggestedQuestions } from '../../../utils/suggestedQuestions';
import { categoriesFor } from '../../../utils/skillCategories';
import { useTranslation } from 'react-i18next';

import ModelSelect from '../modelSelect/ModelSelect';
import { useModelChoice } from '../modelSelect/useModelChoice';
import { useLocale } from '../../../i18n/useLocale';
import {
  gatewayClient,
  UploadedFile,
  type PendingInterrupt,
} from '../../../client/GatewayClient';
import { appConfig, OFFICE_UPLOAD_ENABLED } from '../../../config';
import { gatewayAuthHeaders } from '../../../context/gatewayAuthHeaders';
import Avatar from '@amzn/awsui-chat-components/avatar';
import { Icon } from '@amzn/awsui-components-react';
import { useFilesDragging } from '@amzn/awsui-components-react/polaris/file-dropzone';
import CopilotKitProvider from './CopilotKitProvider';
import { messageWindowMiddleware } from './messageWindowMiddleware';
import { buildReplayMessages } from './replayMessages';
import {
  INLINE_TOOL_NAMES,
  TYPEABLE_GATE_KINDS,
  gateKindForToolName,
  type GateKind,
} from './hitlToolNames';
import FileOutputCards, { fileSentSince, parseFileSendResult } from './FileOutputCards';
import { z } from 'zod';
import {
  AudienceApprovalPanel,
  ContentApprovalPanel,
  GenericApprovalPanel,
  SettledApprovalCard,
  contentPanelPropsFromArgs,
  type GenericApprovalOption,
} from './HumanInterruptPanel';
import './HumanInterruptPanel.css';
import {
  PreviewReviewPanel,
  previewPanelPropsFromArgs,
  SettledPreviewCard,
  type PreviewAction,
} from './PreviewReviewPanel';
import MarkdownRenderer from '../MarkdownRenderer';
import ChatHistory from '../ChatHistory';
import ContextFileBanner from '../ContextFileBanner';
import SkillCard from '../SkillCard';
import PendingQueue, { PendingQuestion } from '../PendingQueue';

const PENDING_QUEUE_MAX = 3;
/** Upper bound on waiting for an in-flight run to detach before switching threads. */
const DETACH_TIMEOUT_MS = 3000;
/** After forcing the transport closed, how much longer to wait for the run to finalize. */
const DETACH_ABORT_GRACE_MS = 500;

/** Whether *promise* settles within *ms*. Never rejects; clears its own timer. */
async function settlesWithin(promise: Promise<unknown>, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise.then(() => true, () => true),
      new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), ms); }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

// Module-local set of agents that already have messageWindowMiddleware attached.
// Using a WeakSet (over Symbol.for on the agent instance) so this state is
// scoped to THIS module — no risk of a third-party package colliding with the
// same registry key, and agents get GC'd normally when the provider unmounts.
const attachedAgents = new WeakSet<object>();

/**
 * Suppresses `CopilotChatMessageView`'s built-in cursor.
 *
 * The slot cannot be omitted — the view renders a wrapper div around whatever the
 * slot resolves to whenever a run is in flight — and the default is a pulsing dot
 * styled with CopilotKit's `cpk:` classes, which we do not load. The panel shows
 * its own "Thinking..." affordance instead, gated on there being no assistant
 * message yet, a condition the cursor slot has no way to express.
 */
const NoCursor = () => <></>;

// ── Durable threadId (task 8.1 / durable-hitl-interrupts) ────────────────────
// Persist threadId keyed by (acting user, agent). Same user + same agent = same
// threadId across a RELOAD, which is the precondition for CP2 recovery: an open
// interrupt on the server is re-delivered on reconnect only if the client
// re-subscribes with the same threadId. Impersonation-aware — the "acting user" is
// `effectiveUid || uid`, so switching impersonation targets picks up that target's
// thread if any (and creates a fresh one if not).
//
// **`sessionStorage`, not `localStorage`, and that is the whole point.** The key is
// per (user, agent), while `localStorage` is shared by every tab of the origin — so
// two tabs did not get two sessions: the second one read this key and landed on the
// FIRST one's thread, and whichever tab last rotated its session overwrote the key
// for both. Two consequences, both observed on dev:
//   • Two clients drove one AgentCore session, so one tab's run could consume the
//     interrupt the other tab was parked on — `respond()` then answered a gate the
//     server was no longer waiting on.
//   • Reassigning `agent.threadId` swaps the message list CopilotKit hands back, so
//     the assistant message carrying an OPEN approval card vanished mid-turn. It came
//     back on reload, because the gate was still parked server-side. That symptom
//     reads exactly like a render bug and is not one — reproduce any "the card
//     disappeared" report with a SINGLE tab before suspecting the render path.
// `sessionStorage` survives a reload (the only durability the recovery path needs) and
// is scoped to one tab, so neither can happen.
//
// ⚠️ WHAT THIS COSTS, accepted deliberately: closing the tab orphans the thread. A
// gate still parked in it is then unreachable from the UI — opening that session from
// ChatHistory does NOT surface a recovery card, because the recovery effect
// deliberately skips explicit history loads (it settles gates rather than recovering
// them). The run stays parked server-side until it times out. If that case ever needs
// covering, the fix is to re-adopt across tab closes without re-introducing sharing:
// hold a Web Lock (`navigator.locks.request(name, {ifAvailable:true})`) for the tab's
// lifetime and adopt a `localStorage` id only when the lock is free.
function threadIdStorageKey(userKey: string, agentName: string): string {
  return `rocky:threadId:${userKey}:${agentName}`;
}
function loadStoredThreadId(userKey: string, agentName: string): string | null {
  if (!userKey || !agentName || typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(threadIdStorageKey(userKey, agentName));
  } catch { return null; }
}
function saveStoredThreadId(userKey: string, agentName: string, threadId: string): void {
  if (!userKey || !agentName || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(threadIdStorageKey(userKey, agentName), threadId);
  } catch { /* storage disabled/full — best-effort */ }
}

// Single source of truth for which uploads the agent can actually consume.
// Images -> image_reader; pdf/doc/docx/xls/xlsx -> Bedrock native document
// block; csv/txt/md/html/json/xml/yaml -> plain text; ppt/pptx -> sandbox
// (python-pptx). Office docs (doc/docx/xls/xlsx) and PowerPoint are gated by
// OFFICE_UPLOAD_ENABLED. The <input accept> attribute only filters the picker,
// NOT drag-and-drop — so processFiles enforces this list at runtime for both.
const BASE_UPLOAD_EXTS = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.pdf', '.csv', '.txt', '.md', '.html', '.json', '.xml', '.yaml', '.yml',
];
const OFFICE_UPLOAD_EXTS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
// OFFICE_UPLOAD_ENABLED is a module-scope constant, so the allowed set never
// changes at runtime — freeze it once at import time instead of rebuilding
// on every render.
const ALLOWED_UPLOAD_EXTS: readonly string[] = OFFICE_UPLOAD_ENABLED
  ? [...BASE_UPLOAD_EXTS, ...OFFICE_UPLOAD_EXTS]
  : BASE_UPLOAD_EXTS;
const ALLOWED_UPLOAD_ACCEPT = ALLOWED_UPLOAD_EXTS.join(',');
import {
  getSkillClient,
  ListEquipmentCommand,
} from '../../../client/SkillClient';
import type { EquipmentItem, CatalogItem } from '../../../client/SkillClient';
import { SessionMessage, type SettledGate } from '../../../client/GatewayClient';
import ToolTrace, { ToolTraceRow } from './ToolTrace';
import {
  coalesceAssistantRuns,
  resolveExtrasByRun,
  turnHasAssistantReply,
  type TranscriptMessage,
} from './transcript';
import {
  RockyAssistantMessage,
  RockyUserMessage,
  TranscriptProvider,
  type MessageExtras,
} from './TranscriptSlots';
import SuggestedQuestions from './SuggestedQuestions';
import SuggestedReplies, {
  isSuggestRepliesError,
  suggestionAnswers,
  usableOptions,
} from './SuggestedReplies';
import { OpenWorkspaceFileContext } from './WorkspaceFileLink';
import { useOpenWorkspaceFile } from '../../../hooks/useOpenWorkspaceFile';
import '../ChatPanel.css';

// ── HITL render adapters ─────────────────────────────────────────────────────
//
// These translate CopilotKit v2's `useHumanInTheLoop` render props into our
// presentational panels. Each adapter:
//   • On `Executing` — mount the interactive panel + record `respond` on the
//     shared ref so external cancel paths (Stop / New Session / Load Session)
//     can settle the gate with a reject verdict rather than leave a dangling
//     tool call in `agent.messages`.
//   • On `Complete`  — render `SettledApprovalCard` (audit trail; stays with
//     the tool-call message in `agent.messages`, rehydrates on session load).
//     Gate 2 re-renders its own panel read-only instead, keeping its rows.
//   • On `InProgress` — reuse the interactive panel with buttons disabled;
//     args are still streaming, but the header/summary are usually usable.
//
// Adapters live at module scope (not inside the component body) because
// `useHumanInTheLoop.render` is a React.ComponentType — recreating it on
// every parent render would remount the card each keystroke.

// `GateKind` drives the canonical reject shape when a gate is settled externally
// (Stop / New Session / Load Session): workflow gates take `{approved:false}`,
// the generic gate takes `{choice:'abort'}`, and the Step 3a preview card, which
// answers in `{action}`, takes `{action:'abort'}`. It lives in `hitlToolNames` next to
// the name list, because the recovered-gate path has to derive it from a bare
// `tool_name` — there are no CopilotKit render props to carry it.

// A gate awaiting the user, addressed by its AG-UI `toolCallId`. `respond` is
// CopilotKit's per-call resolver; it settles that one tool call.
interface PendingGate {
  respond: (v: unknown) => Promise<void>;
  kind: GateKind;
}

// Multi-slot registry of in-flight gates, keyed by toolCallId. Replaces the
// old single-slot ref: with every gate now durable (backend parks each in
// its own native interrupt), a thread can legitimately be parked on more than
// one at once, and a single slot would silently drop all but the last —
// leaving orphaned tool calls that never resolve. A Map lets every external
// cancel settle every open gate, and lets each adapter clean up only its own
// entry on unmount.
type PendingGatesRef = React.MutableRefObject<Map<string, PendingGate>>;

/**
 * Why a gate was settled without approval.
 *
 * A bare `{approved:false}` cannot tell these apart, and the agent needs to act
 * differently on each: 取消 means "stop and talk it over", adjusted (Gate 1's
 * 调整条件, which also carries `adjustment`) means "re-run with these new
 * criteria", view_all (Gate 1's 看全部人的信息) means "export everyone's masked
 * info, then ask again", aborted means the operator left — which is not feedback at all.
 * Ported from mainline (`d85a9d0`), which measured the failure: with the
 * distinction only described in campaign-orchestration's SKILL.md, the agent
 * received the dismissal on the wire and still answered "审批卡片仍在等待你的决定" —
 * pointing the operator at a card that had just been removed.
 *
 * typed means the operator answered in the composer (`TYPEABLE_GATE_KINDS` only);
 * the result also carries `text`, their exact words. It is NOT the former
 * `user_typed`, which dismissed the card AND sent the words as a new user message
 * — a shape that could not be resumed and persisted at once (see `handleSend`).
 * `typed` sends no user message: the words travel only in the tool result.
 */
type GateDismissal = 'cancelled' | 'adjusted' | 'view_all' | 'typed' | 'aborted';

/**
 * What a dismissal has to tell the agent, carried IN THE TOOL RESULT.
 *
 * Deliberately not left to a rule in a skill document: a tool result is read at
 * the moment it matters and cannot be out-competed by a paragraph elsewhere in a
 * 48 KB file. The shared clause across both is that **the card is gone** — only a
 * fresh tool call puts one back — because that is what the agent got wrong.
 *
 * Kept gate-neutral, unlike mainline's audience-specific wording: every gate
 * is durable now, so the same dismissals reach `approve_email_content` and
 * `request_user_approval` too.
 *
 * The note says what to do NEXT. That this is a refusal rather than a success is
 * carried separately and natively, by `error` on the tool message — see
 * `resumeRecoveredGate`.
 */
const DISMISSAL_NOTE: Record<GateDismissal, string> = {
  cancelled:
    'The operator clicked 取消 and the card has been REMOVED from the screen. Nothing ' +
    'was approved and nothing was written. Revise per their feedback, re-present, then ' +
    'call this tool again to put a new card up. Do NOT tell them to click the card — ' +
    'there is none.',
  adjusted:
    'The operator asked to change the audience criteria and the card has been REMOVED. ' +
    'Nothing was approved and nothing was written. Re-run the audience preview applying ' +
    '`adjustment` to the current criteria, present the new count and sample, then call this ' +
    'tool again to put a new card up. Do NOT tell them to click the card — there is none.',
  view_all:
    'The operator asked to see everyone in this audience and the card has been REMOVED. ' +
    'Nothing was approved and nothing was written. Run the masked full export (the ' +
    "audience-segmentation domain's 全量导出 procedure: export_audience_profile.py → " +
    'upload_to_workspace), give the operator the CSV link in one sentence, then call this tool ' +
    'again with the SAME preview values — do not re-run the preview. If the export fails, say ' +
    'so in one sentence and still call this tool again.',
  typed:
    'The operator typed a message instead of clicking, and the card has been REMOVED. Nothing ' +
    'was approved and nothing was written. `text` is their exact words — interpret it: if it ' +
    'asks for a change, apply it as you would an adjustment / revision, then call this tool ' +
    'again with the new values; if it is a question or remark, answer it in one or two ' +
    'sentences, then call this tool again with the SAME values. Typed words are NEVER an ' +
    'approval, even "确认" / "OK" — if they seem to approve, say in one sentence that approval ' +
    'is done with the card\'s button, then call this tool again with the SAME values. Do NOT ' +
    'tell them to click the old card — there is none.',
  aborted:
    'The run was stopped or the session changed; the card has been REMOVED. Do not read ' +
    'this as feedback on what was being approved, and do not call this tool again on your own.',
};

/**
 * The tool result for a gate that was NOT approved.
 *
 * The one place a dismissal payload is built. Two callers need it — the card's
 * cancel button and an external cancel (Stop / New / Load) — and they must agree on
 * the shape, because `request_user_approval` answers with `{choice}`, the two
 * workflow gates with `{approved}`, and the Step 3a preview card with `{action}`
 * (dismissed as `{action:'abort'}`).
 */
function dismissalResult(kind: GateKind, dismissed: GateDismissal): Record<string, unknown> {
  const note = DISMISSAL_NOTE[dismissed];
  if (kind === 'generic') return { choice: 'abort', dismissed, note };
  // The Step 3a card answers in {action}. It has no 取消 button — its modals'
  // cancel only closes the modal — so its dismissals are an abort, or a typed answer.
  if (kind === 'preview') return { action: dismissed === 'typed' ? 'typed' : 'abort', dismissed, note };
  return { approved: false, dismissed, note };
}

/**
 * Answer a gate, and say so in the console.
 *
 * The log is not decoration: mainline's note records two occasions where a card
 * vanished with no click and no typed message, and reading the call sites did not
 * find the cause. `via` is the only thing that separates an operator action from
 * an effect firing twice.
 */
/**
 * The one gate a composer message may answer, or `null` when typing must stay locked.
 *
 * Exactly one open gate in total (live + recovered), of a kind in
 * `TYPEABLE_GATE_KINDS`, with no run in flight. One, because AG-UI requires a
 * resume to address every open interrupt and a sentence cannot be split between
 * cards; no run in flight, because then the card is still streaming its arguments
 * or CopilotKit is already resuming it.
 *
 * Pure, so the render (lock state, placeholder) and `handleSend` (which reads refs)
 * cannot disagree about the answer.
 */
function pickTypeableGate(
  open: readonly { toolCallId: string; kind: GateKind }[],
  recovered: readonly PendingInterrupt[],
  inFlight: boolean,
): { toolCallId: string; kind: GateKind; recovered?: PendingInterrupt } | null {
  if (inFlight) return null;
  const all = new Map<string, { toolCallId: string; kind: GateKind; recovered?: PendingInterrupt }>();
  for (const g of open) all.set(g.toolCallId, g);
  for (const r of recovered) {
    // An unknown tool name still counts as open; 'generic' is never typeable.
    const kind = gateKindForToolName(r.tool_name) ?? 'generic';
    all.set(r.tool_call_id, { toolCallId: r.tool_call_id, kind, recovered: r });
  }
  if (all.size !== 1) return null;
  const [only] = all.values();
  return TYPEABLE_GATE_KINDS.has(only.kind) ? only : null;
}

function settleGate(gate: PendingGate, payload: unknown, via: string): void {
  // eslint-disable-next-line no-console
  console.info('[agui/gate] settle kind=%s via=%s payload=%o', gate.kind, via, payload);
  // Fire-and-forget: the Promise resolves on the next runtime turn, while every
  // caller here is a synchronous UI handler.
  void gate.respond(payload);
}

/** Parse the string `result` payload CopilotKit surfaces on Complete. The
 *  runtime stringifies whatever the frontend passed to `respond()`, so we
 *  JSON.parse defensively and fall back to `unknown` shape. */
function parseGateResult(
  result: string | undefined,
): { approved?: boolean; choice?: string; dismissed?: string; adjustment?: string; text?: string } {
  if (typeof result !== 'string' || !result.trim()) return {};
  try {
    const parsed = JSON.parse(result);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as { approved?: boolean; choice?: string; dismissed?: string; adjustment?: string; text?: string };
    }
  } catch { /* non-JSON result (e.g. plain text) — treat as unknown */ }
  return {};
}

interface AudienceRenderProps {
  status: ToolCallStatus;
  args: Partial<{ segment_description: string; original_query: string; member_count: number; adjust_examples: unknown }>;
  respond?: (v: unknown) => Promise<void>;
  result: string | undefined;
  toolCallId: string;
  pendingGatesRef: PendingGatesRef;
}

/** Defensive: only an array of strings counts as `adjust_examples`. */
function asAdjustExamples(v: unknown): string[] | undefined {
  return Array.isArray(v) && v.every(e => typeof e === 'string') ? (v as string[]) : undefined;
}

function AudienceRenderAdapter(props: AudienceRenderProps) {
  const { status, args, respond, result, toolCallId, pendingGatesRef } = props;
  useEffect(() => {
    if (status === ToolCallStatus.Executing && respond && toolCallId) {
      pendingGatesRef.current.set(toolCallId, { respond, kind: 'audience' });
      return () => {
        // Only remove our own entry, and only if it still points at this
        // respond — a re-render with a fresh respond must not delete the new one.
        const cur = pendingGatesRef.current.get(toolCallId);
        if (cur && cur.respond === respond) {
          pendingGatesRef.current.delete(toolCallId);
        }
      };
    }
    return undefined;
  }, [status, respond, toolCallId, pendingGatesRef]);

  const desc = args?.segment_description ?? args?.original_query;
  const count = args?.member_count;
  if (status === ToolCallStatus.Complete) {
    const { approved, dismissed, adjustment, text } = parseGateResult(result);
    const adjusted = dismissed === 'adjusted';
    const viewAll = dismissed === 'view_all';
    const typed = dismissed === 'typed';
    const adjustmentText = typeof adjustment === 'string' ? adjustment : '';
    const typedText = typeof text === 'string' ? text : '';
    return (
      <SettledApprovalCard
        title="人群审批"
        subtitle={desc}
        metaLines={typeof count === 'number' ? [`人数：${count.toLocaleString()}`] : undefined}
        verdict={adjusted || viewAll || typed ? 'choice' : approved ? 'approved' : 'rejected'}
        verdictText={adjusted
          ? `已调整条件：${adjustmentText.length > 80 ? `${adjustmentText.slice(0, 80)}…` : adjustmentText}`
          : typed ? `💬 已回复：${typedText.length > 80 ? `${typedText.slice(0, 80)}…` : typedText}`
          : viewAll ? '已导出全部人的信息' : undefined}
      />
    );
  }
  return (
    <AudienceApprovalPanel
      segmentDescription={desc}
      memberCount={count}
      adjustExamples={asAdjustExamples(args?.adjust_examples)}
      active={status === ToolCallStatus.Executing}
      onApprove={() => respond && settleGate({ respond, kind: 'audience' }, { approved: true }, 'approve-button')}
      onReject={() => respond && settleGate({ respond, kind: 'audience' }, dismissalResult('audience', 'cancelled'), 'cancel-button')}
      onAdjust={(t) => respond && settleGate(
        { respond, kind: 'audience' },
        { ...dismissalResult('audience', 'adjusted'), adjustment: t },
        'adjust-button',
      )}
      onViewAll={() => respond && settleGate({ respond, kind: 'audience' }, dismissalResult('audience', 'view_all'), 'view-all-button')}
    />
  );
}

/**
 * Reads a workspace file's text from the chat owner's workspace. That is the
 * signed-in user, except in Admin View: there the chat — and the file the agent
 * wrote — belong to the impersonated user, so `user` must name them, as every
 * other workspace/history read in this panel does. Memoized so the card sees one
 * loader per (agent, owner).
 */
function useCsvLoader(agentName: string) {
  const { isImpersonating, effectiveUid } = useImpersonation();
  const user = isImpersonating ? effectiveUid : undefined;
  return useMemo(
    () => async (path: string): Promise<string> => (await gatewayClient.getWorkspaceFile(agentName, path, user)).content,
    [agentName, user],
  );
}

interface ContentRenderProps {
  agentName: string;
  status: ToolCallStatus;
  args: Record<string, unknown>;
  respond?: (v: unknown) => Promise<void>;
  result: string | undefined;
  toolCallId: string;
  pendingGatesRef: PendingGatesRef;
}

function ContentRenderAdapter(props: ContentRenderProps) {
  const { agentName, status, args, respond, result, toolCallId, pendingGatesRef } = props;
  const loadCsv = useCsvLoader(agentName);
  useEffect(() => {
    if (status === ToolCallStatus.Executing && respond && toolCallId) {
      pendingGatesRef.current.set(toolCallId, { respond, kind: 'content' });
      return () => {
        const cur = pendingGatesRef.current.get(toolCallId);
        if (cur && cur.respond === respond) {
          pendingGatesRef.current.delete(toolCallId);
        }
      };
    }
    return undefined;
  }, [status, respond, toolCallId, pendingGatesRef]);

  // The settled card keeps the review rows: the card is the only place they exist.
  const verdict = status === ToolCallStatus.Complete
    ? (parseGateResult(result).approved ? 'approved' : 'rejected')
    : undefined;
  const panelProps = contentPanelPropsFromArgs(args);
  return (
    <ContentApprovalPanel
      {...panelProps}
      // Withheld while the args stream: partial-JSON parsing hands over a truncated
      // path, and fetching it is a guaranteed 404.
      batchCsvPath={status === ToolCallStatus.InProgress ? undefined : panelProps.batchCsvPath}
      loadCsv={loadCsv}
      verdict={verdict}
      active={status === ToolCallStatus.Executing}
      onApprove={() => respond && settleGate({ respond, kind: 'content' }, { approved: true }, 'approve-button')}
      onReject={() => respond && settleGate({ respond, kind: 'content' }, dismissalResult('content', 'cancelled'), 'cancel-button')}
    />
  );
}

interface GenericRenderProps {
  status: ToolCallStatus;
  args: Partial<{
    title: string;
    message: string;
    options: GenericApprovalOption[];
    context: Record<string, unknown>;
  }>;
  respond?: (v: unknown) => Promise<void>;
  result: string | undefined;
  toolCallId: string;
  pendingGatesRef: PendingGatesRef;
}

function GenericRenderAdapter(props: GenericRenderProps) {
  const { status, args, respond, result, toolCallId, pendingGatesRef } = props;
  useEffect(() => {
    if (status === ToolCallStatus.Executing && respond && toolCallId) {
      pendingGatesRef.current.set(toolCallId, { respond, kind: 'generic' });
      return () => {
        const cur = pendingGatesRef.current.get(toolCallId);
        if (cur && cur.respond === respond) {
          pendingGatesRef.current.delete(toolCallId);
        }
      };
    }
    return undefined;
  }, [status, respond, toolCallId, pendingGatesRef]);

  if (status === ToolCallStatus.Complete) {
    const { choice } = parseGateResult(result);
    // Match the settled choice value back to its label (best-effort — falls
    // back to the raw value if the option list is unavailable).
    const chosen = args?.options?.find((o) => o.value === choice);
    return (
      <SettledApprovalCard
        title={args?.title ?? '确认'}
        subtitle={args?.message}
        verdict={choice === 'abort' ? 'rejected' : 'choice'}
        choiceLabel={chosen?.label ?? choice ?? '—'}
      />
    );
  }
  return (
    <GenericApprovalPanel
      title={args?.title}
      message={args?.message}
      options={(args?.options ?? []) as GenericApprovalOption[]}
      active={status === ToolCallStatus.Executing}
      onChoose={(value) => respond && settleGate({ respond, kind: 'generic' }, { choice: value }, 'choice-button')}
    />
  );
}

interface PreviewRenderProps {
  status: ToolCallStatus;
  args: Record<string, unknown>;
  respond?: (v: unknown) => Promise<void>;
  result: string | undefined;
  toolCallId: string;
  pendingGatesRef: PendingGatesRef;
}

function PreviewRenderAdapter(props: PreviewRenderProps) {
  const { status, args, respond, result, toolCallId, pendingGatesRef } = props;
  useEffect(() => {
    if (status === ToolCallStatus.Executing && respond && toolCallId) {
      pendingGatesRef.current.set(toolCallId, { respond, kind: 'preview' });
      return () => {
        const cur = pendingGatesRef.current.get(toolCallId);
        if (cur && cur.respond === respond) {
          pendingGatesRef.current.delete(toolCallId);
        }
      };
    }
    return undefined;
  }, [status, respond, toolCallId, pendingGatesRef]);

  const data = previewPanelPropsFromArgs(args);
  if (status === ToolCallStatus.Complete) {
    return <SettledPreviewCard data={data} result={result} />;
  }
  return (
    <PreviewReviewPanel
      {...data}
      active={status === ToolCallStatus.Executing}
      onAction={(a: PreviewAction) =>
        respond && settleGate({ respond, kind: 'preview' }, a, `preview-${a.action}`)}
    />
  );
}

// Renders a gate recovered from the server after a reload. Unlike the live
// adapters above (which are driven by CopilotKit's `useHumanInTheLoop` render
// props and settle via `respond()`), a recovered gate has no live `respond` —
// the page that owned it is gone. It is always interactive (`active`), and the
// answer is sent by `onResume`, which appends the trailing tool message and
// re-runs. Argument field names are the tool's own snake_case params, matching
// what the live adapters read from `args`.
//
// 取消 goes through `dismissalResult`, exactly as the live adapters do. A bare
// `{approved:false}` here would re-create the failure DISMISSAL_NOTE exists to
// fix — the agent reads a plain rejection, revises, and tells the operator to
// click a card that was removed on click. Nothing about that failure is specific
// to the live path, so nothing about the fix can be either.
function RecoveredGateCard({
  interrupt,
  agentName,
  onResume,
}: {
  interrupt: PendingInterrupt;
  agentName: string;
  onResume: (interrupt: PendingInterrupt, value: unknown, opts?: { refused?: boolean }) => void;
}) {
  // Above the early return below: hooks must run on every render.
  const loadCsv = useCsvLoader(agentName);
  const args = interrupt.arguments ?? {};
  const get = <T,>(key: string): T | undefined => args[key] as T | undefined;
  const kind = gateKindForToolName(interrupt.tool_name);
  // Not one of the known gates — the Runtime should never park on anything else,
  // and there is no card shape to render. Also narrows `kind` for the branches.
  if (!kind) return null;

  switch (interrupt.tool_name) {
    case 'approve_audience_segment':
      return (
        <AudienceApprovalPanel
          segmentDescription={get<string>('segment_description') ?? get<string>('original_query')}
          memberCount={get<number>('member_count')}
          adjustExamples={asAdjustExamples(get<string[]>('adjust_examples'))}
          active
          onApprove={() => onResume(interrupt, { approved: true })}
          onReject={() => onResume(interrupt, dismissalResult(kind, 'cancelled'), { refused: true })}
          onAdjust={(t) => onResume(
            interrupt,
            { ...dismissalResult(kind, 'adjusted'), adjustment: t },
            { refused: true },
          )}
          onViewAll={() => onResume(interrupt, dismissalResult(kind, 'view_all'), { refused: true })}
        />
      );
    case 'approve_email_content':
      return (
        <ContentApprovalPanel
          {...contentPanelPropsFromArgs(args)}
          loadCsv={loadCsv}
          active
          onApprove={() => onResume(interrupt, { approved: true })}
          onReject={() => onResume(interrupt, dismissalResult(kind, 'cancelled'), { refused: true })}
        />
      );
    case 'request_user_approval':
      return (
        <GenericApprovalPanel
          title={get<string>('title')}
          message={get<string>('message')}
          options={(get<GenericApprovalOption[]>('options') ?? []) as GenericApprovalOption[]}
          active
          // Bare `{choice}`, matching the live adapter: the generic panel has no
          // cancel button — every button is one of the agent's own options — so a
          // dismissal reaches this gate only through an abort (Stop / New / Load),
          // which goes through `dismissalResult` at its own call site. Typing used to
          // be the other way in; the composer is locked while a card is open now.
          onChoose={(value) => onResume(interrupt, { choice: value })}
        />
      );
    case 'review_email_preview':
      return (
        <PreviewReviewPanel
          {...previewPanelPropsFromArgs(args)}
          active
          onAction={(a) => onResume(interrupt, a)}
        />
      );
    default:
      return null;
  }
}

// ── Types ───────────────────────────────────────────────────────────────────


// Campaign Copilot's starter prompts: a mail-stats question, a full campaign brief
// (objective / persona / schedule / SFDC campaign) that walks the orchestration flow,
// and a light one.
const CAMPAIGN_COPILOT_SUGGESTED_QUESTIONS: string[] = [
  '帮我查下一下近一个月的邮件发送情况',
  'Business Objective: Customers with personalized email campaigns.\n' +
  'Targeting Persona: BMW公司的IT Executive\n' +
  'Schedule: StartTime: 2026-09-16, EndTime: 2027-09-16, Frequency: 11:00 AM UTC+8\n' +
  'CampaignId:701RU00000FvYM5YAN, Program Type: Lead Nurture - Other',
  '今天心情特别好，帮我写一条庆祝文案',
];

interface AgUiChatPanelProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  /** Hide the fullscreen toggle button. Used by surfaces (e.g. IVT Hub) that
   *  always want the panel in side-by-side mode. Defaults to false. */
  hideFullscreen?: boolean;
  /** AG-UI agent id registered on the runtime (e.g. 'campaign_copilot_agent'). */
  agentName: string;
}


// ── Thought collapsible block (committed messages) ──────────────────────────


// ToolTrace / SuggestedQuestions / FileOutputCards are imported from their
// extracted modules above — they were previously duplicated inline here.

/**
 * Derive the display-extras side table from persisted history.
 *
 * Restored history contributes exactly one extra: the ⏱ badge, computed from the
 * gap between a user_query's `create_time` and the `agent_final_response` that
 * answered it. Everything else on the transcript now comes straight from
 * `agent.messages` (which `buildReplayMessages` reconstructs from the same rows),
 * so this no longer builds a parallel message list — the projection that used to
 * live here, and drift from `agent.messages`, is gone.
 *
 * Keyed by the final response's own `message_id`. That id is NOT necessarily the
 * one the rendered bubble carries — a turn that called a tool replays as
 * `[<toolRow>-call, <toolRow>-result, <finalResponse>]` and coalesces onto the
 * first fragment — which is what `resolveExtrasByRun` re-keys.
 *
 * Used by both the initial history load and scroll-up pagination, so the two
 * never diverge.
 */
function sessionMessagesToExtras(msgs: SessionMessage[]): Map<string, MessageExtras> {
  const extras = new Map<string, MessageExtras>();
  let lastUserTime: number | undefined;

  for (const m of msgs) {
    if (m.message_type === 'user_query') {
      lastUserTime = new Date(m.create_time).getTime();
    } else if (m.message_type === 'agent_final_response') {
      if (lastUserTime === undefined) continue;
      const elapsedS = (new Date(m.create_time).getTime() - lastUserTime) / 1000;
      if (elapsedS > 0) {
        extras.set(m.message_id, { elapsedS: Math.round(elapsedS * 100) / 100 });
      }
    }
  }
  return extras;
}


// ── Component ───────────────────────────────────────────────────────────────

/**
 * AgUiChatPanel — public entry point.
 *
 * Wraps <CopilotKitProvider> around <AgUiChatPanelInner> so useAgent /
 * useCopilotKit inside the inner component find the client. All UI/state
 * lives in the inner component and mirrors ChatPanelLegacy — see the
 * "AG-UI SEAM" markers for the only places where behavior differs.
 */
export default function AgUiChatPanel(props: AgUiChatPanelProps) {
  return (
    <CopilotKitProvider>
      <AgUiChatPanelInner {...props} />
    </CopilotKitProvider>
  );
}

function AgUiChatPanelInner({ isCollapsed, onToggle, hideFullscreen, agentName }: AgUiChatPanelProps) {
  // AG-UI SEAM: data source. `agent` gives us live messages + isRunning;
  // `copilotkit.runAgent` triggers a send. Everything below this hook is a
  // byte-for-byte copy of ChatPanelLegacy (UI, state, refs, callbacks).
  const { agent, isReady: agentIsReady } = useAgent({ agentId: agentName });
  const { copilotkit } = useCopilotKit();

  // Attach the outbound-messages window middleware once per agent instance.
  // Without this, a rich turn 1 (skill_load + manage_domain + file_read
  // returning large content) pushes the RunAgentInput past AgentCore's
  // 64 KB per-frame WS limit on turn 2, closing the socket with 1009
  // before the run reaches the Runtime. See messageWindowMiddleware.ts.
  //
  // agent.use() accumulates middlewares, so we guard against re-attaching
  // when the effect re-runs (StrictMode / Fast Refresh / provider re-mount).
  useEffect(() => {
    if (!agent) return;
    if (attachedAgents.has(agent)) return;
    agent.use(messageWindowMiddleware);
    attachedAgents.add(agent);
  }, [agent]);

  // AG-UI SEAM: Human-in-the-Loop approval gates (CopilotKit v2 pattern).
  //
  // The Campaign Copilot orchestration SOP declares FOUR approval tools driven
  // from the Portal:
  //   • Gate 1  approve_audience_segment  — on the audience preview, before it is saved
  //   • Gate 2  approve_email_content     — before create_octopus_program
  //   • Generic request_user_approval     — every other user-confirmation branch
  //     (tool retry/skip, partial-success continue-vs-retry, destructive proceed).
  //     N-choice (2–4 buttons), returns {choice}.
  //   • Step 3a review_email_preview      — sample-email preview before the batch; returns {action}
  //
  // All four use `useHumanInTheLoop` (CopilotKit v2). The `render` prop
  // receives a discriminated `status` union:
  //   • `InProgress` — args are still streaming from the LLM; show a compact
  //                    "preparing" placeholder or the interactive card with
  //                    buttons disabled.
  //   • `Executing`  — args complete, `respond({...})` is available; show
  //                    the interactive card with active buttons.
  //   • `Complete`   — `result` string is populated; show the settled read-
  //                    only card (this is the audit trail — the card stays
  //                    with the tool-call message in `agent.messages`, so
  //                    it persists across session load and history nav).
  //
  // Because this panel is a custom chat UI (not `<CopilotChat>`), we do NOT
  // get automatic mounting. Instead we call `useRenderToolCall()` and iterate
  // `agent.messages` ourselves to drive each registered `render` — see the
  // `hitlCardsByToolCallId` map below; each card renders inside the message it
  // belongs to, matching CopilotKit's own per-message `toolCallsView`.
  //
  // The old `approve_execution_plan` tool has been removed — the SOP no
  // longer gates the plan step.

  // Registry of gates currently awaiting the user, keyed by toolCallId. Each
  // render adapter registers its own `respond` while Executing and removes it
  // on unmount, so external cancel paths (Stop / New Session / Load Session)
  // can settle EVERY open gate — not just the most recent. Every gate is
  // now durable (the backend parks each in its own native interrupt), so a
  // thread can legitimately be parked on more than one; the old single slot
  // dropped every gate but the last, orphaning the others.
  const pendingGatesRef = useRef<Map<string, PendingGate>>(new Map());

  // HITL gates recovered from the server after a page reload. Populated by the
  // recovery effect (far below) when the durable-interrupt query finds this
  // thread parked on one or more gates whose interactive cards were lost with
  // the tab. Rendered as standalone cards (the tool calls are NOT in
  // `agent.messages` until /gate-history fills them in, so the normal
  // per-message card path can't rebuild them). Declared here so `cancelPendingApproval` below can
  // clear them from one place.
  const [recoveredGates, setRecoveredGates] = useState<PendingInterrupt[]>([]);
  // Read-side mirror, for the same reason `pendingGatesRef` is a ref: `handleSend`
  // and the queue-drain effect have to know whether a card is on screen WITHOUT
  // taking it as a dependency — re-creating `handleSend` on every recovery would
  // invalidate the drain effect and the keydown handler with it. Assigned during
  // render, matching `chatWidthRef` above.
  const recoveredGatesRef = useRef<PendingInterrupt[]>(recoveredGates);
  recoveredGatesRef.current = recoveredGates;
  // Forward reference: `handleSend` answers a recovered card through
  // `resumeRecoveredGate`, which is declared far below it.
  const resumeRecoveredGateRef = useRef<
    ((interrupt: PendingInterrupt, value: unknown, opts?: { refused?: boolean }) => Promise<void>) | null
  >(null);
  // One recovery query per threadId, latched only once a query comes back
  // AUTHORITATIVE. Without the guard the effect refires on every threadId-sync /
  // re-render and re-queries (or clobbers gates the user is mid-answer on);
  // latching an `unavailable` answer instead hid a real parked gate for the rest
  // of the page's life. See the effect for both halves.
  const recoveryCheckedThreadRef = useRef<string | null>(null);
  // Forward references, same device as `handleStopRef` below: `dropRecoveredGates` has
  // to re-ask, but both the thread id and the query are declared further down.
  const sessionIdRef = useRef<string>('');
  const checkPendingInterruptsRef = useRef<((threadId: string) => Promise<void>) | null>(null);

  // Guards the async settled-gate fetch in handleLoadSession. A slow reply for
  // session A must not overwrite agent.messages after the user has already
  // loaded session B. It says nothing about a live turn — `handleSend` bumps
  // `sendGenerationRef`, a different counter — which is why `applyReplay` below
  // protects live messages by identity rather than by generation.
  const settledGatesGenRef = useRef(0);

  // ── Replay inputs ───────────────────────────────────────────────────
  //
  // The persisted rows and gate verdicts that `agent.messages` is rebuilt from,
  // held in refs rather than captured in a closure. Three call sites contribute
  // at three different times — the initial page (handleLoadSession), the verdicts
  // (the /gate-history pass), and each older page (loadOlderMessages) — and the
  // one that arrives last used to rebuild from ITS OWN snapshot, discarding
  // whatever the other two had added. That is how a paginated page lost its
  // approval cards, and how a verdict landing a second after a history load
  // erased a question the operator had typed in the meantime.
  const replayRowsRef = useRef<SessionMessage[]>([]);
  const replayGatesRef = useRef<SettledGate[]>([]);
  // Whether `replayRowsRef` covers the conversation from its very first message
  // with nothing trimmed off the end — the precondition for pairing gate rows to
  // verdicts by position. See `planToolCalls`.
  const replayHistoryCompleteRef = useRef(false);
  // Did the loaded session end in an incomplete turn we trimmed off? Kept
  // separately because `loadOlderMessages` re-derives completeness when it
  // exhausts `next_token`, and the tail verdict is decided once, at load.
  const replayTailTrimmedRef = useRef(false);

  // Fire every pending gate's `respond` with its canonical reject value. Used
  // by Stop / New Session / Load Session so the tool calls resolve cleanly and
  // their cards transition to a settled state (rather than staying "Executing"
  // forever after we blow away agent state).
  /**
   * Settle every open gate, saying why.
   *
   * `dismissed` defaults to `'aborted'` because that is what the session-boundary
   * callers (Stop / New / Load) mean. It is NOT a bare rejection: telling the agent
   * the operator rejected the audience would have it revise and re-ask on a run
   * they just stopped — the only other producer is the card's own 取消 button.
   *
   * Returns whether anything was settled, so the caller can tell a dismissal from
   * a no-op without reading the ref twice.
   */
  const cancelPendingApproval = useCallback((dismissed: GateDismissal = 'aborted') => {
    // Recovered gates are NOT cleared here — see `dropRecoveredGates`. This used
    // to do it, on the grounds that one call should give every session-boundary
    // handler a clean slate, and that cost a real failure: the history bootstrap
    // races the recovery query on reload and calls this for the very thread the
    // recovered card belongs to, so whichever resolved second wiped it.
    const gates = pendingGatesRef.current;
    if (gates.size === 0) return false;
    const settling = Array.from(gates.values());
    gates.clear();
    for (const gate of settling) {
      settleGate(gate, dismissalResult(gate.kind, dismissed), dismissed);
    }
    return true;
  }, []);

  /**
   * Take the recovered cards off the screen without answering them.
   *
   * Separate from `cancelPendingApproval` because dropping a recovered gate is not
   * settling it: it has no `respond`, so nothing reaches the runtime and the run
   * stays parked server-side. Which means the ONLY honest thing to do is let the
   * recovery query find it again — hence the latch release. Without it, a card
   * dropped here was gone for the life of the page (Stop cleared it, and the effect
   * refused to re-ask), leaving a parked run the operator could not answer.
   *
   * Call it where the operator has genuinely moved on — Stop, New Session, loading a
   * DIFFERENT session — and not when merely re-reading the current thread's history.
   */
  const dropRecoveredGates = useCallback(() => {
    if (recoveredGatesRef.current.length === 0) return;
    recoveryCheckedThreadRef.current = null;
    setRecoveredGates([]);
    // Ask again, rather than hoping the recovery effect re-runs: nothing it depends on
    // changes when the operator clicks Stop. Deferred to a microtask so the caller
    // (handleStop / handleNewSession) has finished rotating `sessionId` first — a
    // fresh thread has nothing parked, and asking about the one just abandoned would
    // put its card back on a conversation the operator left.
    const threadId = sessionIdRef.current;
    void Promise.resolve().then(() => {
      if (sessionIdRef.current !== threadId) return;
      void checkPendingInterruptsRef.current?.(threadId);
    });
  }, []);

  // Gate 1 — Audience approval, on the PREVIEW (before the audience is saved).
  useHumanInTheLoop({
    name: 'approve_audience_segment',
    description:
      'Request human approval for an audience the operator has just previewed. ' +
      'Call this AFTER audience-segmentation shows the preview (a count plus a small sample of rows) ' +
      'and BEFORE anything is saved — approval is what authorises the save, and the Octopus push after it. ' +
      'Pass a short description (original_query or segment_description) and the total member count. ' +
      'Do NOT pass sample rows — the delegated skill already printed them in chat. ' +
      'Returns {approved: true} — then YOU save the audience and push it. Or {approved: false} plus a ' +
      '`note` saying what to do next, and nothing has been written. READ THAT NOTE: the card is gone from ' +
      'the screen in every non-approved case, so never tell the operator to click it, and calling this tool ' +
      'again is the only way to put a new card up. ' +
      '{approved: false, dismissed: "adjusted", adjustment} means the operator gave new criteria — ' +
      're-run the preview applying `adjustment`, then call this tool again. ' +
      '{approved: false, dismissed: "view_all"} means the operator wants everyone\'s (masked) info — ' +
      'run the full masked export, give the link, then call this tool again with the same preview ' +
      'values (do not re-run the preview). ' +
      '{approved: false, dismissed: "typed", text} means the operator typed `text` instead of clicking — ' +
      'a change → apply it and call this tool again; a question or remark → answer briefly and call this ' +
      'tool again with the same values. Typed text is never an approval.',
    parameters: z.object({
      segment_description: z.string().optional().describe('One-line description of the audience (usually the original NL query).'),
      original_query: z.string().optional().describe('Fallback for segment_description if not set.'),
      member_count: z.number().optional().describe('Total people the preview matched (the preview COUNT).'),
      adjust_examples: z.array(z.string()).max(3).optional().describe('Up to 3 short adjustment examples for THIS audience, phrased as the operator would type them (e.g. 只保留 IT 相关角色). Shown as the 调整条件 placeholder. No PII.'),
      // NOTE: no `sample_customers` — the sample rows already live in chat
      // (the delegated `audience-segmentation` skill prints its own preview
      // table). Duplicating rows here bloats LLM context on resume and loses
      // the audit trail whenever the card unmounts on click.
    }),
    render: (props) => (
      <AudienceRenderAdapter
        {...props}
        pendingGatesRef={pendingGatesRef}
      />
    ),
  });

  // Gate 2 — Email content batch approval (before create_octopus_program).
  // The card is the whole batch review and loads every row from batch_result.csv
  // itself, so the model passes counts and the path only. Copying sample / flagged
  // rows into the arguments was ~3.5k output tokens / ~35 s per call; the schema no
  // longer offers them. Gates settled under the older row arguments still replay
  // (`contentPanelPropsFromArgs` reads them).
  useHumanInTheLoop({
    name: 'approve_email_content',
    description:
      'Request human approval for the batch-generated email content before creating the Octopus program. ' +
      'Call this AFTER email-content-personalization batch-generates content for ALL customer_ids[] ' +
      '(per-recipient realize + EBR check complete) and BEFORE create_octopus_program. ' +
      'The card IS the batch review. Do NOT also print the batch table in chat — one lead-in sentence is enough. ' +
      'Upload batch_result.csv to the workspace first and pass its path as batch_csv_path, plus the counts: the card ' +
      'loads and filters every recipient from that file, and approval waits until it has. Never pass rows. ' +
      'No uploaded CSV is an error: report it instead of calling this tool. ' +
      'Returns {approved: true} to create the program. Or {approved: false} plus a `note` saying what to do ' +
      'next: the card is gone from the screen in every non-approved case, so never tell the operator to click ' +
      'it — calling this tool again is the only way to put a new card up.',
    parameters: z.object({
      content_plan_id: z.string().optional().describe('The frozen content_plan_id from email-content-personalization.'),
      total_count: z.number().optional()
        .describe('The FULL audience count (member_count).'),
      ebr_histogram: z.record(z.string(), z.number()).optional()
        .describe('Count per ebr_status across the WHOLE batch, every bucket including pending and missing, ' +
          'e.g. {"pass": 1790, "pass_with_warning": 20, "hold": 4, "pending": 1}.'),
      failed_count: z.number().optional().describe('Recipients whose generation failed (status != OK).'),
      batch_csv_path: z.string().describe('REQUIRED. Workspace-relative path of the uploaded batch_result.csv (output/campaign-copilot/email-preview/<ai_campaign_id>/batch_result.csv), verbatim. Never invented.'),
    }),
    render: (props) => (
      <ContentRenderAdapter
        {...props}
        agentName={agentName}
        pendingGatesRef={pendingGatesRef}
      />
    ),
  });

  // Generic — n-choice user approval. Covers every user-confirmation branch
  // that isn't one of the dedicated gates above (tool retry/skip,
  // partial-success continue-vs-retry, destructive proceed, …).
  useHumanInTheLoop({
    name: 'request_user_approval',
    description:
      'Ask the operator to pick between N (2–4) explicit choices. Use this for ANY user-confirmation ' +
      'branch that is NOT one of the dedicated gates (approve_audience_segment / approve_email_content / review_email_preview) — ' +
      'tool-call retry vs skip, partial-success continue vs retry-failed, destructive proceed, etc. ' +
      'Do NOT emit a "请选择 / 回复 xxx" text table alongside this call — the buttons ARE the UI. ' +
      'Returns { choice: <the selected option.value> }. Branch on `value` (never parse `label`). ' +
      'A dismissal comes back as { choice: "abort" } plus a `note`: the card is gone from the screen, so ' +
      'never tell the operator to click it — call this tool again to put a new one up.',
    parameters: z.object({
      title: z.string().optional().describe('Short header line (bold). e.g. "create_octopus_segment 调用失败".'),
      message: z.string().optional()
        .describe('Markdown body — cause / impact / guidance. Keep concise; the buttons carry the choices.'),
      options: z
        .array(
          z.object({
            value: z.string().describe(
              'Stable short id the agent branches on (e.g. `retry` / `skip` / `continue` / `retry_failed` / `abort`).',
            ),
            label: z.string().describe('User-facing button text (may include emoji).'),
            kind: z.enum(['primary', 'secondary', 'danger', 'ghost']).optional()
              .describe('Visual hint. Use `danger` to mark destructive-proceed options like "忽略失败继续".'),
          }),
        )
        .min(2)
        .max(4)
        .describe('2–4 choices, rendered left-to-right in the order given.'),
      context: z.record(z.string(), z.unknown()).optional()
        .describe('Optional metadata (tool_name, fail_items, …). Echoed for debugging; not shown to the user.'),
    }),
    render: (props) => (
      <GenericRenderAdapter
        {...props}
        pendingGatesRef={pendingGatesRef}
      />
    ),
  });

  // Step 3a — the sample-email preview, before the batch. Not a commit gate (Gate 2
  // still is), but it parks: the batch must not start until the operator picks.
  useHumanInTheLoop({
    name: 'review_email_preview',
    description:
      'Show the Step 3a sample-email preview card and wait for the operator. Call this right after ' +
      'email-content-personalization renders and uploads the single sample preview (preview_and_upload.py), ' +
      'and again after EVERY adjustment re-renders it. Never start the batch before this returns ' +
      '{action:"approve"}. The card IS the preview UI: do NOT print the preview field table or ' +
      '修改/换风格/换模板 chips in chat — one lead-in sentence is enough. Pass `templates` from ' +
      'list_registered_templates() (include the current one) and `current_style` as a style key ' +
      '("professional" when none was chosen yet). Returns one of: ' +
      '{action:"approve"} → freeze the plan and run the batch; ' +
      '{action:"change_style", style, extra?} → refine_plan with the instruction the domain maps that style key to ' +
      '(plus `extra` when present), re-preview, call this again; ' +
      '{action:"change_template", template_id} → re-run plan_personalization with that template, re-preview, call this again; ' +
      '{action:"revise", feedback} → refine_plan from the feedback, re-preview, call this again; ' +
      '{action:"typed", text} → the operator typed `text` instead of clicking: a change → handle it like revise; ' +
      'a question or remark → answer briefly and call this again with the same values; never an approval; ' +
      '{action:"abort"} plus a `note` → the run was stopped and the card is gone; do not call this tool again on your own.',
    parameters: z.object({
      subject: z.string().optional().describe('Subject line returned by preview_and_upload.py.'),
      portal_url: z.string().optional().describe('Preview URL returned by preview_and_upload.py, verbatim.'),
      sample_recipient: z.string().optional().describe('Display name of the sample recipient.'),
      current_template_id: z.string().optional().describe('template_id the current plan is bound to.'),
      templates: z.array(z.object({
        template_id: z.string(),
        template_name: z.string().optional(),
        content_type: z.string().optional(),
      }).passthrough()).optional()
        .describe('Every eligible template from list_registered_templates(), including the current one.'),
      current_style: z.string().optional()
        .describe('Style key: professional | executive_brief | friendly | data_driven | action_oriented | educational.'),
      content_plan_id: z.string().optional().describe('The draft plan-… id.'),
      plan_version: z.number().optional(),
      member_count: z.number().optional().describe('Full audience size.'),
      cta_url: z.string().optional().describe('The complete CTA URL confirmed in step 1, including ?trk=.'),
      ebr_status: z.string().optional().describe("The sample's throwaway EBR result, if run."),
    }),
    render: (props) => (
      <PreviewRenderAdapter
        {...props}
        pendingGatesRef={pendingGatesRef}
      />
    ),
  });

  // Every other tool call — the wildcard (`name: "*"`) registration CopilotKit
  // consults only when no tool-specific renderer matched
  // (`useRenderToolCall`: exact match first, `"*"` last). So the gates
  // above keep their approval panels and everything else becomes a trace row.
  //
  // This is the hook that retired ThoughtBlock. We used to serialise each tool
  // call into `[TOOL_START]name\n k=v[/TOOL]` in three separate places and parse
  // it back with ~90 lines of regex; CopilotKit already carries the structured
  // form, with a real lifecycle status instead of our hard-coded `|success`.
  useDefaultRenderTool({
    render: ({ name, parameters, status }) => (
      <ToolTraceRow name={name} parameters={parameters} status={status} />
    ),
  });

  // `file_send` (a Runtime tool): the file card, at its place in the reply, once
  // the tool result names the file. Until then — and for a result that names no
  // file (an `{"error"}`, or replay's `{"restored":true}` stand-in) — the same
  // row the wildcard would draw. Clicks go through a ref because the handler is
  // declared further down, next to the workspace navigation it drives.
  const openFileRef = useRef<(path: string) => void>(() => {});
  // Stable, for OpenWorkspaceFileContext: gate cards' workspace links open through it.
  const openFileViaRef = useCallback((path: string) => openFileRef.current(path), []);
  const openWorkspaceFile = useOpenWorkspaceFile();
  useRenderTool({
    name: 'file_send',
    parameters: z.object({
      category: z.string().optional(),
      filename: z.string().optional(),
    }),
    render: ({ name, parameters, status, result }) => {
      const file = status === 'complete' ? parseFileSendResult(result) : null;
      if (!file) return <ToolTraceRow name={name} parameters={parameters} status={status} />;
      return <FileOutputCards files={[file]} onFileClick={(path) => openFileRef.current(path)} />;
    },
  });

  // `suggest_replies` (a Runtime no-op tool): clickable reply options. Not a gate —
  // the run does not park and the composer stays open. A click sends the label
  // exactly as if typed; the card is spent once ANY user message follows it
  // (`suggestionAnswers`, read through a ref because `toolCallsById` memoizes on
  // `toolCallSignal`, which carries the answers). Rendered from the arguments, never
  // the result, so restored history (`{"restored":true}`) draws the same card.
  const suggestionAnswersRef = useRef<Map<string, string>>(new Map());
  const sendReplyRef = useRef<(text: string) => void>(() => {});
  useRenderTool({
    name: 'suggest_replies',
    parameters: z.object({
      options: z.array(z.object({ label: z.string(), description: z.string().optional() })).optional(),
      prompt: z.string().optional(),
    }),
    render: ({ name, toolCallId, parameters, status, result }) => {
      if (status === 'complete' && isSuggestRepliesError(result)) {
        return <ToolTraceRow name={name} parameters={parameters} status={status} />;
      }
      return (
        <SuggestedReplies
          options={usableOptions(parameters?.options)}
          prompt={parameters?.prompt}
          answer={suggestionAnswersRef.current.get(toolCallId)}
          pending={status === 'inProgress'}
          onSelect={(label) => sendReplyRef.current(label)}
        />
      );
    },
  });

  // Renders a single tool call into a React element by consulting the
  // registered tool renderers above. CopilotKit maps (toolMessage present?) →
  // Complete vs Executing and calls our registered `render` accordingly.
  const renderToolCall = useRenderToolCall();

  // Every tool call in the thread, rendered on demand and keyed by `toolCallId`.
  //
  // Mirrors CopilotKit's own composition rather than a global block:
  // `CopilotChatAssistantMessage` renders `[markdownRenderer, toolCallsView,
  // toolbar]`, and `CopilotChatToolCallsView` renders only the tool calls of the
  // message it was given. Collecting elements into one array and rendering it
  // after the transcript — which is what we used to do for approval cards —
  // detaches a card from the message that produced it, so every card piled up at
  // the bottom of the thread regardless of when its gate was answered.
  //
  // `agent.messages` supplies each element's CONTENT; the display projection
  // supplies its POSITION via `DisplayMessage.toolCallIds`. `name` is carried
  // alongside so the render site can split "needs attention" (approval cards,
  // shown) from "background detail" (trace rows, collapsed) — a product
  // distinction CopilotKit does not model, which is why the split lives at the
  // one render site rather than in the dispatch.
  const rawMessages = (agent?.messages ?? []) as Array<{
    id: string;
    role: string;
    toolCallId?: string;
    content?: unknown;
    toolCalls?: Array<{
      id: string;
      type?: 'function';
      function?: { name?: string; arguments?: string };
    }>;
  }>;

  /**
   * What `toolCallsById` is keyed on — deliberately NOT `agent.messages`.
   *
   * The array's identity is not a change signal, for the same reason the
   * transcript above does not memoize on it: `addMessage` pushes onto the existing
   * array. The specific failure is the one that matters most here — when
   * CopilotKit splices in the `role:'tool'` result after `respond()`, a memo keyed
   * on identity can keep the stale element, so the card stays `Executing` with
   * live buttons after it has been answered and a second click calls `respond`
   * twice. (`useRenderToolCall`'s own identity changes with
   * `executingToolCallIds`, which masks this some of the time — an intermittent
   * double-settle is worse to debug than a consistent one.)
   *
   * Keyed instead on what decides the output: which tool calls exist, how far
   * their arguments have streamed, and which ones now have a result.
   */
  const toolCallSignal = rawMessages
    .map((m) => {
      if (m.role === 'tool' && typeof m.toolCallId === 'string') return `r:${m.toolCallId}`;
      if (m.role === 'assistant' && Array.isArray(m.toolCalls)) {
        return m.toolCalls
          .map((tc) => `c:${tc?.id}:${tc?.function?.arguments?.length ?? 0}`)
          .join(',');
      }
      return '';
    })
    .filter(Boolean)
    .join('|');
  // `suggest_replies` cards change state when a user message follows them, which
  // the signal above cannot see — append the answers so the memo re-renders them.
  const suggestionAnswersNow = suggestionAnswers(rawMessages as TranscriptMessage[]);
  suggestionAnswersRef.current = suggestionAnswersNow;
  const toolCallSignalWithAnswers = suggestionAnswersNow.size === 0
    ? toolCallSignal
    : `${toolCallSignal}#${[...suggestionAnswersNow].map(([id, a]) => `${id}=${a}`).join('|')}`;

  // Read through a ref so the memo can be keyed on the signal above while still
  // rendering from the CURRENT messages — listing `rawMessages` as a dependency
  // would put the unreliable identity back in charge.
  const rawMessagesRef = useRef(rawMessages);
  rawMessagesRef.current = rawMessages;

  const toolCallsById = useMemo(() => {
    const raw = rawMessagesRef.current;
    const toolMessagesById = new Map<string, ToolMessage>();
    for (const m of raw) {
      if (m.role === 'tool' && typeof m.toolCallId === 'string') {
        toolMessagesById.set(m.toolCallId, m as unknown as ToolMessage);
      }
    }
    const byId = new Map<string, { name: string; element: React.ReactElement }>();
    for (const m of raw) {
      if (m.role !== 'assistant' || !Array.isArray(m.toolCalls)) continue;
      for (const tc of m.toolCalls) {
        const name = tc?.function?.name;
        if (!name) continue;
        const el = renderToolCall({
          toolCall: tc as unknown as ToolCall,
          toolMessage: toolMessagesById.get(tc.id),
        });
        // React needs a stable key across renders — toolCallId is unique per
        // tool call (per the AG-UI schema) and stable across the run.
        if (el) {
          byId.set(tc.id, {
            name,
            element: <React.Fragment key={tc.id}>{el}</React.Fragment>,
          });
        }
      }
    }
    return byId;
  }, [toolCallSignalWithAnswers, renderToolCall]);

  /**
   * Which HITL gates are waiting on the operator, according to `agent.messages`?
   *
   * Every assistant-declared frontend gate with no `role:'tool'` result yet.
   * Recovered gates are tracked separately (`recoveredGates`). Derived from the messages rather than read off
   * `pendingGatesRef`, because that registry is written by a card's mount effect —
   * and the drawer guard below needs the answer precisely when no card is mounted.
   */
  const openGates = useMemo(() => {
    const raw = rawMessagesRef.current;
    const settled = new Set(
      raw.filter((m) => m.role === 'tool' && typeof m.toolCallId === 'string')
        .map((m) => m.toolCallId as string),
    );
    const open: { toolCallId: string; kind: GateKind }[] = [];
    for (const m of raw) {
      if (m.role !== 'assistant' || !Array.isArray(m.toolCalls)) continue;
      for (const tc of m.toolCalls) {
        const kind = gateKindForToolName(tc?.function?.name ?? '');
        if (kind && !settled.has(tc.id)) open.push({ toolCallId: tc.id, kind });
      }
    }
    return open;
  }, [toolCallSignal]);
  const openGatesRef = useRef(openGates);
  openGatesRef.current = openGates;
  const gateAwaitingAnswer = openGates.length > 0;
  // Read-side mirror, so `handleSend` can refuse a send without taking this as a
  // dependency (which would re-create it — and the drain effect and keydown handler
  // with it — on every message change).
  const gateAwaitingAnswerRef = useRef(gateAwaitingAnswer);
  gateAwaitingAnswerRef.current = gateAwaitingAnswer;

  /**
   * Split a message's tool calls into the cards it must surface (approval gates
   * and result cards, `INLINE_TOOL_NAMES`) and the trace rows it can collapse.
   *
   * The one place that decision is made. Both the committed transcript and the
   * in-flight streaming bubble call this, so a gate can never be shown as a
   * trace row in one and a card in the other — which is exactly the shape of the
   * duplicate-rendering bug this feature already produced twice.
   */
  const splitToolCalls = useCallback((toolCallIds: string[] | undefined) => {
    const gates: React.ReactElement[] = [];
    const trace: React.ReactElement[] = [];
    for (const id of toolCallIds ?? []) {
      const entry = toolCallsById.get(id);
      if (!entry) continue;
      (INLINE_TOOL_NAMES.has(entry.name) ? gates : trace).push(entry.element);
    }
    return { gates, trace };
  }, [toolCallsById]);


  const { uid, display_name } = useAuth();
  const isRocky = agentName === 'rocky';
  const { isImpersonating, effectiveUid } = useImpersonation();
  const { senderInfo } = useUserProfile();
  const { metadata } = useReportMetadata();
  const { selectedFile, refreshWorkspace } = useWorkspaceNav();
  const { pendingPrompt, clearPendingPrompt, draftPrompt, clearDraftPrompt, setChatBusy, setChatQueueFull, chatFullscreen, toggleFullscreen, assetScope, setAssetScope, ucScope, setUcScope } = useChatPanel();
  // AG-UI: local sessionId mirrors agent.threadId. The runtime persists messages
  // to DynamoDB keyed by agent.threadId (via BedrockAgentCoreContext); local
  // sessionId is used by /upload and /stop, which must target the same key
  // or files/cancels land in the wrong session. The "rocky-" prefix keeps DDB
  // rows visually attributable to this app.
  //
  // Cross-reload durability (task 8.1): try localStorage first so same
  // (user, agent) resumes the same thread across browser closes. The initializer
  // runs on the very first render — uid may still be empty here if AuthContext
  // hasn't resolved yet; the hydration effect below covers that async case.
  // Pure initializer — StrictMode double-invokes it, so any side effect (like
  // writing agent.threadId or localStorage) belongs in a useEffect, not here.
  const userKey = effectiveUid || uid;
  const [sessionId, setSessionId] = useState(() => {
    const stored = loadStoredThreadId(userKey, agentName);
    return stored ?? `rocky-${crypto.randomUUID()}`;
  });
  // Persist sessionId under (userKey, agentName). Handles three cases:
  //   1. userKey resolves after first render → adopt stored id if present.
  //   2. sessionId changes (new chat / history load / SDK adopt) → persist.
  //   3. userKey switches (impersonation change) → adopt target's stored id
  //      if present; otherwise persist current sessionId under new key.
  // hydratedForUserRef tracks whether we've already run the "adopt from LS"
  // step for the current userKey, so a later setSessionId (fresh from a new
  // chat click) doesn't loop back and re-adopt the stored id.
  sessionIdRef.current = sessionId;
  const hydratedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userKey) return;
    if (hydratedForUserRef.current !== userKey) {
      hydratedForUserRef.current = userKey;
      const stored = loadStoredThreadId(userKey, agentName);
      if (stored && stored !== sessionId) {
        setSessionId(stored);
        if (agent) agent.threadId = stored;
        return;
      }
    }
    saveStoredThreadId(userKey, agentName, sessionId);
  }, [userKey, agentName, sessionId, agent]);
  // Sync agent.threadId ↔ sessionId. If the SDK-generated threadId is already
  // one of ours (rocky-<uuid>), adopt it; otherwise overwrite with sessionId.
  useEffect(() => {
    if (!agent) return;
    if (agent.threadId === sessionId) return;
    if (agent.threadId?.startsWith('rocky-')) {
      setSessionId(agent.threadId);
      return;
    }
    agent.threadId = sessionId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, agent?.threadId, sessionId]);
  const { locale } = useLocale();
  // The hook's t (as ChatPanelLegacy passes) is a new function on every language
  // change, so the welcome follows a switch without a dependency the memo never reads.
  const { t: tChat } = useTranslation('chat');
  const welcomeMessage = useMemo(() => getAgentWelcome(agentName, tChat), [agentName, tChat]);
  /**
   * Per-message display data that does not exist in `agent.messages` — the KB /
   * context-file chips on a user message, the ⏱ badge on an answer. Keyed by the
   * id of the message it belongs to.
   *
   * State rather than a ref because a write must repaint: the chips are decided
   * at send time and the badge arrives with a history fetch, both independent of
   * any change to `agent.messages`.
   */
  const [extras, setExtras] = useState<Map<string, MessageExtras>>(() => new Map());

  /**
   * What the transcript renders. `agent.messages` with each run of assistant
   * fragments merged into one message, so one answer gets one bubble and one
   * avatar (see `coalesceAssistantRuns`).
   *
   * Deliberately NOT memoized on `agent.messages`. The array's identity is not a
   * reliable signal that its contents changed: `addMessage` PUSHES onto the
   * existing array and notifies asynchronously, while only a run's apply-events
   * frames replace it. Keyed on identity, the user's own message stayed invisible
   * until the first streamed frame and then appeared together with the answer.
   *
   * Recomputing per render is a linear pass over a list the render walks anyway,
   * and `coalesceAssistantRuns` returns the original object for every message it
   * did not merge, so message identity — and with it CopilotKit's per-message
   * memoization — survives for all but the run heads.
   */
  const { messages: transcriptMessages, mergedIds } =
    coalesceAssistantRuns((agent?.messages ?? []) as TranscriptMessage[]);
  /** `extras`, re-keyed onto the ids the merged messages actually carry. */
  const resolvedExtras = resolveExtrasByRun(extras, mergedIds);

  // `unplacedGateCards` used to live here: a net for approval cards whose message
  // the transcript was withholding. There is no such state any more — every tool
  // call belongs to a message, and every message is rendered, including the one
  // in flight. The net and the streaming-side tool-call list both go with it.

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  /**
   * Whether a turn is in flight — the ONE answer to that question.
   *
   * Two consumers must agree exactly: the mirror's strip (which rows are
   * withheld from the transcript) and the streaming bubble's mount condition
   * (where those withheld rows render). They used to read different flags —
   * `agent.isRunning` and `isLoading` — and the window where those disagree is
   * real: `runAgent` sets `isRunning` synchronously, then awaits `onInitialize`
   * before `onRunInitialized` fires our `setIsLoading(true)`. Answering a gate
   * lands in exactly that window, and the withheld rows rendered NOWHERE — the
   * approval card vanished until the page was reloaded.
   *
   * The union, not either one: `isLoading` leads on a user send (set optimistically
   * before `runAgent`), `isRunning` leads on a resume CopilotKit drives itself.
   */
  const isTurnInFlight = isLoading || !!agent?.isRunning;
  const [showHistory, setShowHistory] = useState(false);
  // Approval cards render inside the `!showHistory` branch. If a gate arrives
  // while the operator is browsing chat history, snap back to the chat view so the
  // card actually mounts — otherwise the run is parked on a click they cannot make.
  //
  // Reads `gateAwaitingAnswer`, derived from `agent.messages`, NOT
  // `pendingGatesRef`: that registry is populated by a card's own mount effect,
  // and while the drawer is open the transcript — and therefore every card — is
  // unmounted, so the registry is empty in exactly the case this guard is for. It
  // could never fire; the operator had to notice and close the drawer themselves.
  //
  // Only a gate that ARRIVES closes the drawer. Keying on "a gate is waiting" made
  // the drawer impossible to open from a thread parked on one — it closed in the
  // same render it opened, so the history list flashed and vanished. The operator
  // already saw that card; opening history from it is a deliberate move away.
  const waitingGateIds = useMemo(
    () => [
      ...openGates.map((g) => g.toolCallId),
      ...recoveredGates.map((g) => g.tool_call_id),
    ],
    [openGates, recoveredGates],
  );
  const seenWaitingGateIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const seen = seenWaitingGateIdsRef.current;
    const arrived = waitingGateIds.some((id) => !seen.has(id));
    seenWaitingGateIdsRef.current = new Set(waitingGateIds);
    if (showHistory && arrived) setShowHistory(false);
  }, [waitingGateIds, showHistory]);
  useEffect(() => { setChatBusy(isLoading); }, [isLoading, setChatBusy]);

  // Track whether user has chatted since the last suggestion-reset event
  // (session start or report selection). Controls suggested-question visibility.
  const [hasChatted, setHasChatted] = useState(false);
  // Whether to show default (non-report) suggested questions.
  // True only at session start; set to false once a report is selected.
  const [showDefaultSuggestions, setShowDefaultSuggestions] = useState(true);

  // ── File attachment state ───────────────────────────────────────────
  // Unified pending file model — avoids parallel array desync (AutoSDE #1)
  interface PendingFile {
    id: string;         // stable key
    file: File;         // original File object
    status: 'uploading' | 'done' | 'error';
    uploaded?: UploadedFile;
  }
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  // Derive uploading state from pendingFiles — reactive, no manual bookkeeping
  const isUploading = pendingFiles.some(pf => pf.status === 'uploading');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // True while the user is dragging file(s) anywhere over the window — used to
  // reveal the drop overlay only when there's actually a file to drop.
  const { areFilesDragging } = useFilesDragging();
  // Whether the selected workspace file is included as context
  const [contextFileEnabled, setContextFileEnabled] = useState(true);
  // Track whether the context file label has been shown in chat (only show on first send)
  const contextFileLabelShownRef = useRef(false);
  // Track which asset-scope id has already been labeled on a user message. On
  // (re)bind — including clearing to null then re-binding — the id differs from
  // the last labeled one and the next user message re-shows the "KB: <name>" tag.
  const assetScopeLabelShownForRef = useRef<string | null>(null);
  // UC parallel of assetScopeLabelShownForRef (keyed by solutionMapId as a string).
  const ucScopeLabelShownForRef = useRef<string | null>(null);
  // The "label shown" refs are only advanced on SEND, never on (re)bind/clear — so
  // without this, switching back to a previously-bound scope (or clearing then
  // rebinding the same one) would leave a stale ref that suppresses the "KB: <name>"
  // message tag. This effect fires ONLY when a scope's identity changes (bind /
  // rebind / clear — sends don't change scope), so resetting both refs here is
  // exactly "show the tag once per (re)bind" for asset, UC, and switch-backs alike.
  useEffect(() => {
    assetScopeLabelShownForRef.current = null;
    ucScopeLabelShownForRef.current = null;
  }, [assetScope, ucScope]);

  // Streaming states — separate from messages array for RAF-speed updates

  // ── Resize state ────────────────────────────────────────────────────
  const [chatWidth, setChatWidth] = useState(407);
  const [isResizing, setIsResizing] = useState(false);
  const chatWidthRef = useRef(407);
  chatWidthRef.current = chatWidth;

  // ── Message pagination (older messages on scroll-to-top) ─────────
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const messagesNextTokenRef = useRef<string | undefined>(undefined);
  const historySessionIdRef = useRef<string | undefined>(undefined);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // `skipNextAgentSyncRef` and the historical-thought map used to live here. Both
  // existed to reconcile the parallel `DisplayMessage[]` with `agent.messages` —
  // one suppressed the mirror's next pass so hydrated rows were not flattened, the
  // other re-applied thought strings the mirror would otherwise drop. The mirror is
  // gone and the transcript IS `agent.messages`, so both are too; restored rows now
  // carry their tool calls as real messages instead of a string on the side.
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Model picker: options + the user's global choice, sent as forwardedProps.model.
  const modelChoice = useModelChoice();
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const sendGenerationRef = useRef(0);
  // Bumped by every thread switch (history load / new session). A switch that has
  // to wait for the in-flight run to detach checks it before applying, so a newer
  // switch made during the wait wins.
  const switchGenRef = useRef(0);
  const conversationIdRef = useRef<string>('');
  // Flips true once the runtime has produced its first streaming payload
  // (text delta or tool-call start). Legacy parity: handleNewSession only
  // prompts "still connecting, switch may lose the reply?" during the
  // pre-first-payload window; once content is landing, the switch is silent.
  const runtimeConfirmedRef = useRef(false);

  // ── Pending question queue (sent while agent is streaming) ──────────
  const [pendingQueue, setPendingQueue] = useState<PendingQuestion[]>([]);
  const drainRef = useRef(false);
  const [queueToast, setQueueToast] = useState<string | null>(null);
  const queueToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showQueueToast = useCallback((msg: string) => {
    if (queueToastTimerRef.current) clearTimeout(queueToastTimerRef.current);
    setQueueToast(msg);
    queueToastTimerRef.current = setTimeout(() => setQueueToast(null), 2500);
  }, []);
  useEffect(() => () => {
    if (queueToastTimerRef.current) clearTimeout(queueToastTimerRef.current);
  }, []);
  // Mirror queue-full state to context so external triggers (e.g.
  // WorkspaceHome cards) can early-return before starting any animation.
  useEffect(() => {
    setChatQueueFull(pendingQueue.length >= PENDING_QUEUE_MAX);
  }, [pendingQueue.length, setChatQueueFull]);

  // ── Equipment / Skill Pack state ────────────────────────────────────
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);

  // Campaign Copilot keeps its campaign walkthrough examples; every other agent gets
  // the same per-agent defaults ChatPanelLegacy shows (no session count here, so the
  // portal-usage question is never mixed in). The defaults are picked at random, once
  // per agent and session (a new chat gets a fresh set, as on ChatPanelLegacy), as
  // { zh, en } pairs; the locale is applied at render so switching language shows the
  // same starters translated rather than a fresh random set. Campaign Copilot's fixed
  // starters are Chinese-only, as they were before.
  const isCampaignCopilot = agentName === 'campaign_copilot_agent';
  const pickedQuestions = useMemo(
    () => (isCampaignCopilot ? [] : getDefaultSuggestedQuestions(null, agentName)),
    // sessionId re-picks per chat; it is not read inside on purpose.
    [agentName, isCampaignCopilot, sessionId],
  );
  const defaultSuggestedQuestions =
    isCampaignCopilot
      ? CAMPAIGN_COPILOT_SUGGESTED_QUESTIONS
      : pickedQuestions.map((q) => q[locale]);


  // The current agent's categories (utils/skillCategories), in display order.
  const agentCategories = useMemo(() => categoriesFor(agentName), [agentName]);

  // Group by category label ('DataRetriever', 'Campaign Copilot', ...), the key
  // SkillCard looks its avatar and description up by.
  const groupedSkills = useMemo(() => {
    const labelByCategory = new Map(agentCategories.map((c) => [c.apiCategory as string, c.label]));
    const map = new Map<string, CatalogItem[]>();
    for (const item of equipmentItems) {
      if (!item.skill?.category) continue;
      const cat = labelByCategory.get(item.skill.category.toLowerCase()) ?? item.skill.category;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item.skill);
    }
    return map;
  }, [equipmentItems, agentCategories]);

  // Rocky keeps its fixed three-slot pack (greyed when empty). Every other agent
  // shows one card per configured category it actually has skills in.
  const packCategories = useMemo(
    () => (isRocky ? [] : agentCategories.filter((c) => groupedSkills.has(c.label))),
    [isRocky, agentCategories, groupedSkills],
  );
  const showPack = isRocky || packCategories.length > 0;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  // Reset hasChatted when a different report is selected so suggested questions reappear
  // Reset hasChatted when a different report is selected so suggested questions reappear.
  // Also disable default suggestions once a report is selected.
  useEffect(() => {
    setHasChatted(false);
    setContextFileEnabled(true);
    contextFileLabelShownRef.current = false;
    if (selectedFile) {
      setShowDefaultSuggestions(false);
    }
    // Intentionally key on `.key` only, not the full `selectedFile` object:
    // the parent context may recreate the wrapper object (same file, refreshed
    // metadata) while the user is mid-conversation, and firing this effect on
    // every object-identity change would clobber `hasChatted` and reset the
    // context-file label — exactly what we don't want. Reset only on a real
    // file-selection change (key changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile?.key]);

  // Scroll to bottom when suggested questions appear (file selected + metadata loaded)
  useEffect(() => {
    if (!hasChatted && selectedFile && metadata?.suggested_questions?.length) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) {
            isProgrammaticScrollRef.current = true;
            userScrolledUpRef.current = false;
            setUserScrolledUp(false);
            container.scrollTop = container.scrollHeight;
            lastScrollTopRef.current = container.scrollTop;
            requestAnimationFrame(() => {
              isProgrammaticScrollRef.current = false;
            });
          }
        });
      });
    }
  }, [hasChatted, selectedFile?.key, metadata?.suggested_questions]);

  // ── Load equipped skills ──────────────────────────────────────────
  // The current agent's: with agentName, ListEquipment returns that agent's whole
  // catalog minus what the user unequipped (default-equipped), each item carrying
  // its catalog entry, so a user who never equipped anything still gets a pack.
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSkillClient().send(
          new ListEquipmentCommand({ userAlias: uid, agentName, maxResults: 100 }),
        );
        if (import.meta.env.DEV) console.log('[ChatPanel] ListEquipment response:', resp);
        if (!cancelled && resp.items) {
          setEquipmentItems(resp.items);
        }
      } catch (err) {
        console.warn('[ChatPanel] Failed to load equipment:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, agentName]);

  // ── Skill card handlers ───────────────────────────────────────────
  const handleSkillSelect = useCallback((displayName: string) => {
    setInputText(`Execute ${displayName} skill`);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  // ── Drag-to-resize ────────────────────────────────────────────────
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Clean up any prior session whose mouseup was missed (e.g. focus loss)
    resizeCleanupRef.current?.();
    const startX = e.clientX;
    const startWidth = chatWidthRef.current;

    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.min(Math.max(startWidth + delta, 280), 700);
      setChatWidth(newWidth);
    };

    const cleanup = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizeCleanupRef.current = null;
    };

    const onMouseUp = () => cleanup();

    resizeCleanupRef.current = cleanup;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // ── Smart auto-scroll: only scroll if user is near the bottom ─────
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const userScrolledUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);

  /**
   * A value that changes when the TRANSCRIPT changes, and not otherwise.
   *
   * `transcriptMessages` cannot be that value: it is deliberately rebuilt every
   * render (see above), so an effect keyed on it fires on every render — a
   * keystroke, a resize, a toast. Each pass set `isProgrammaticScrollRef` and
   * cleared it two frames later, so during any burst of renders the flag was
   * effectively pinned true, `handleMessagesScroll` early-returned on it, and
   * `userScrolledUp` never got set: scrolling up to re-read an earlier answer
   * while composing snapped straight back to the bottom.
   *
   * Derived from what actually moves the scroll height: how many messages there
   * are, and how much text they hold in total.
   *
   * Summed over ALL messages, not read off the last one. `coalesceAssistantRuns`
   * emits a turn's merged assistant head BEFORE the tool rows it produced, so on
   * any turn that called a tool the last entry is a `role:'tool'` row with fixed
   * content while the text that is actually growing sits on the head. Keyed on the
   * tail, the effect never fired during those turns and a long answer streamed in
   * below the fold.
   */
  let transcriptChars = 0;
  let transcriptToolCalls = 0;
  for (const m of transcriptMessages) {
    if (typeof m.content === 'string') transcriptChars += m.content.length;
    transcriptToolCalls += m.toolCalls?.length ?? 0;
  }
  const transcriptSignal = `${transcriptMessages.length}:${transcriptChars}:${transcriptToolCalls}`;

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        isProgrammaticScrollRef.current = true;
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
          lastScrollTopRef.current = container.scrollTop;
          requestAnimationFrame(() => {
            isProgrammaticScrollRef.current = false;
          });
        });
      }
    }
  }, [transcriptSignal, pendingQueue.length]);

  // When messages change (streaming end commit) or loading stops, check if still at bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !userScrolledUpRef.current) return;
    requestAnimationFrame(() => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 50) {
        userScrolledUpRef.current = false;
        setUserScrolledUp(false);
      }
    });
  }, [transcriptSignal, isLoading]);

  // ── Auto-resize textarea ──────────────────────────────────────────

  const [inputHeight, setInputHeight] = useState<number | null>(null);
  const inputResizingRef = useRef(false);
  const inputResizeStartYRef = useRef(0);
  const inputResizeStartHeightRef = useRef(0);

  const handleInputResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    inputResizingRef.current = true;
    inputResizeStartYRef.current = e.clientY;
    const wrapper = (e.target as HTMLElement).parentElement;
    inputResizeStartHeightRef.current = wrapper?.offsetHeight ?? 80;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!inputResizingRef.current) return;
      const delta = inputResizeStartYRef.current - ev.clientY;
      const newHeight = Math.max(80, Math.min(inputResizeStartHeightRef.current + delta, 300));
      setInputHeight(newHeight);
    };

    const handleMouseUp = () => {
      inputResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const syncTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el || inputHeight !== null) return;
    if (!inputText) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [inputHeight, inputText]);

  useEffect(() => {
    syncTextareaHeight();
  }, [inputText, syncTextareaHeight]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  }, []);

  // ── AG-UI SEAM #1: data source ────────────────────────────────────
  // In the legacy panel this position holds `consumeSSEStream` (~170 lines)
  // that parses SSE events into local streaming buffers. For AG-UI, CopilotKit
  // owns the transport and exposes a live `agent.messages` array +
  // `agent.isRunning`; the mirror effect below folds those into the same
  // `messages` / `isLoading` / `streamingText` states
  // the rest of this component already uses — so ToolTrace / bubbles /
  // typing indicator / stop button all work unchanged.

  // AG-UI subscribe: three responsibilities in one subscription.
  //  (1) Run lifecycle → isLoading. onRunInitialized fires right before the
  //      run streams, onRunFinalized fires from the SDK's finally() block on
  //      success / error / abort — one source of truth, no ref latch, no
  //      "Thinking..." flicker during the WS handshake gap.
  //  (2) Streaming text, driven directly by the SDK's incremental text events.
  //      Tool calls are NOT tracked here: `agent.messages` already carries them
  //      with parsed args and a real status, and `processApplyEvents` replaces
  //      the messages array on every applied event (@ag-ui/client
  //      `AbstractAgent`), so the mirror effect below sees each delta and
  //      derives `streamingToolCallIds` from it. The previous version kept a
  //      parallel ref of name + raw-arg buffers here and serialised it into a
  //      tagged string — two sources for one fact, and the string had to be
  //      kept byte-compatible with the history renderer's.
  //  (3) CUSTOM 'session_metadata' capture for per-turn conversation_id.
  //      onEvent (catch-all) is used because CopilotKit v2 does not reliably
  //      dispatch through per-type CUSTOM callbacks — filtering by event.type
  //      is the stable path.
  useEffect(() => {
    if (!agent || typeof agent.subscribe !== 'function') return;
    const sub = agent.subscribe({
      onRunInitialized: ({ input }: { input?: { runId?: unknown } }) => {
        setIsLoading(true);
        // Name the turn a Stop targets from the moment the run starts. The
        // runtime keys the turn on this same runId (it is the run's
        // conversation_id), and the Gateway accepts it on /stop. Waiting for the
        // CUSTOM `session_metadata` below left a gap: the runtime holds
        // RUN_STARTED until the first content frame, so a Stop during the first
        // model call went out untargeted — and an untargeted cancel flag matches
        // ANY turn on the session, including the user's next one.
        const runId = input?.runId;
        if (typeof runId === 'string' && runId) conversationIdRef.current = runId;
      },
      onRunFinalized: () => { setIsLoading(false); },
      // Text is no longer buffered here: `agent.messages` carries the partial
      // content (the apply-events reducer appends each delta and re-emits a
      // cloned array), and the transcript renders that message directly. This
      // callback survives only as the "the runtime answered" signal for the
      // email-fallback prompt.
      onTextMessageContentEvent: () => { runtimeConfirmedRef.current = true; },
      // Kept only as the "the runtime answered" signal — a turn that calls a
      // tool before emitting any text would otherwise look unreachable to the
      // fallback-prompt logic. The tool call itself is rendered off
      // `agent.messages`.
      onToolCallStartEvent: () => { runtimeConfirmedRef.current = true; },
      onEvent: ({ event }: { event: { type?: string; name?: string; value?: unknown } }) => {
        if (event?.type !== 'CUSTOM') return;
        if (event.name !== 'session_metadata') return;
        const val = event.value as { conversation_id?: unknown } | undefined;
        const cid = val?.conversation_id;
        if (typeof cid === 'string' && cid) {
          conversationIdRef.current = cid;
        }
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  // The committed-messages mirror used to live here: ~200 lines projecting
  // `agent.messages` into a parallel `DisplayMessage[]`, coalescing assistant
  // fragments, re-deriving tool-call positions, and re-attaching display-only
  // metadata by id. `CopilotChatMessageView` renders `agent.messages` directly
  // now, so there is nothing to keep in sync — which is the point. Three of the
  // five bugs this feature produced were the projection and the source of truth
  // disagreeing.


  // ── File attachment handlers ──────────────────────────────────────────

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Core upload path — shared by the file picker and the drag-and-drop
  // dropzone. Takes a plain File[] so both entry points reuse the same
  // type/size validation and S3 upload logic.
  const processFiles = useCallback(async (selected: File[]) => {
    const files = [...selected];
    if (!files.length) return;

    // Allowlist enforcement — the agent can only read ALLOWED_UPLOAD_EXTS.
    // This runs for BOTH the picker and drag-and-drop (the <input accept>
    // attribute does not filter dropped files), so e.g. .zip/.exe/.mp4
    // dropped onto the panel are rejected here rather than uploaded and
    // then silently unreadable. Office/PowerPoint are included only when
    // OFFICE_UPLOAD_ENABLED.
    const isAllowed = (f: File) => ALLOWED_UPLOAD_EXTS.some(ext => f.name.toLowerCase().endsWith(ext));
    const rejected = files.filter(f => !isAllowed(f));
    if (rejected.length > 0) {
      const exts = [...new Set(rejected.map(f => f.name.split('.').pop()?.toUpperCase() || '?'))].join('、');
      alert(`暂不支持上传 ${exts} 类型的文件，请转换为 PDF、CSV、TXT 等支持的格式后重试。`);
      const supported = files.filter(isAllowed);
      if (!supported.length) return;
      // Continue with supported files only
      files.length = 0;
      files.push(...supported);
    }

    // Create PendingFile entries with stable IDs (AutoSDE #1)
    const newEntries: PendingFile[] = files.map(f => ({
      id: crypto.randomUUID(),
      file: f,
      status: 'uploading' as const,
    }));
    setPendingFiles(prev => [...prev, ...newEntries]);

    try {
      const result = await gatewayClient.uploadFiles(files, sessionId);
      // Match uploaded results back to pending entries by index
      setPendingFiles(prev => prev.map(pf => {
        const idx = newEntries.findIndex(ne => ne.id === pf.id);
        if (idx === -1) return pf;  // not from this batch
        const uploaded = result.files[idx];
        return uploaded
          ? { ...pf, status: 'done' as const, uploaded }
          : { ...pf, status: 'error' as const };
      }));
      if (result.errors.length > 0) {
        console.warn('Upload errors:', result.errors);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setPendingFiles(prev => prev.map(pf =>
        newEntries.some(ne => ne.id === pf.id) ? { ...pf, status: 'error' as const } : pf
      ));
    } finally {
    }
  }, [sessionId]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset input so re-selecting the same file triggers onChange (AutoSDE #5)
    if (fileInputRef.current) fileInputRef.current.value = '';
    void processFiles(files);
  }, [processFiles]);

  // Dropzone onChange gives the current File[] value directly.
  const handleFileDrop = useCallback((files: File[]) => {
    void processFiles(files);
  }, [processFiles]);

  const handleRemoveFile = useCallback((id: string) => {
    setPendingFiles(prev => prev.filter(pf => pf.id !== id));
  }, []);

  const handleSend = useCallback(async (text?: string, opts?: { fromQueue?: boolean }) => {
    const rawText = text ?? inputText;
    const sendText = rawText.trim();
    // Guard: block empty send / mid-upload (AutoSDE #6)
    if (!sendText || isUploading) return;

    // An open approval card blocks sending outright. The composer is disabled for
    // the same condition, so this is the belt to that braces — a queued drain, an
    // auto-send from a workspace card, or Enter landing in the gap before a repaint
    // all reach here without going through the textarea.
    //
    // Why refusing beats answering the gate on the operator's behalf, which is what
    // this used to do ("typing past a card" dismissed it with a `user_typed`
    // verdict): the adapter can only turn a client tool result into a resume when
    // that result is the LAST thing in the payload (`ag_ui_strands.agent`, the
    // trailing scan feeding `bridged_resume_entries`). A turn that carries both a
    // dismissal and a new user message cannot satisfy that and also leave the user
    // message last, which is what the Runtime keys "persist this question" on. One
    // shape had the gate silently never resumed, the other lost the question from
    // history — measured both ways. Not producing that shape at all removes the
    // dilemma, and with it the client-only marker the middleware needed to carry it.
    //
    // The escape hatch is 取消 on the card, Stop, or New Session — or, for a single
    // `TYPEABLE_GATE_KINDS` card, the typed-answer branch above, which sends no user
    // message at all.
    //
    // A drained message is PUT BACK rather than dropped. The drain effect pops the
    // head before it calls here, so returning without re-enqueueing loses it — and
    // the two conditions are not identical by design: the drain reads the mounted-card
    // registry while this reads the message-derived one, so a collapsed panel or an
    // open history drawer (both unmount every card) lets the drain through to here.
    // The old code excluded `fromQueue` from the dismissal path for the same reason.
    // A typed answer to the one open card (`TYPEABLE_GATE_KINDS`). The words go in
    // the gate's tool result and NOTHING else is sent — no user message — so the
    // payload still ends in that tool result, the only shape the adapter resumes
    // from (see the refusal below for the shape that fails). Only the composer's
    // own text qualifies (`text === undefined`): a queued message was typed before
    // the card existed, and an auto-send or a reply chip is not an answer to it.
    // Pending attachments stay in the composer for the next ordinary message.
    if (text === undefined && !opts?.fromQueue) {
      const target = pickTypeableGate(
        openGatesRef.current, recoveredGatesRef.current, isLoading || !!agent?.isRunning,
      );
      const live = target && !target.recovered ? pendingGatesRef.current.get(target.toolCallId) : undefined;
      if (target && (target.recovered || live)) {
        const payload = { ...dismissalResult(target.kind, 'typed'), text: sendText };
        // Not `refused`: a typed answer carries real input, so it is a resolved
        // interrupt, not a cancelled one — the same success the live card sends.
        if (target.recovered) void resumeRecoveredGateRef.current?.(target.recovered, payload);
        else if (live) settleGate(live, payload, 'composer');
        setInputText('');
        if (textareaRef.current) textareaRef.current.style.height = '';
        return;
      }
    }

    if (gateAwaitingAnswerRef.current || recoveredGatesRef.current.length > 0) {
      if (opts?.fromQueue) {
        setPendingQueue(prev => [{ id: crypto.randomUUID(), text: sendText, createdAt: Date.now() }, ...prev]);
      } else {
        showQueueToast(tChat('queue.gateFirst'));
      }
      return;
    }

    // While a turn is in flight: enqueue instead of sending. The drain effect
    // re-invokes handleSend with { fromQueue: true } once the turn ends (natural
    // end or stop), so this branch only fires for fresh user input.
    //
    // `isTurnInFlight`, not `isLoading`, and read HERE rather than off the render
    // — `agent.isRunning` flips synchronously inside `runAgent` while
    // `setIsLoading(true)` waits for `onInitialize` to resolve, so a value
    // captured at render time is stale for exactly the window this guard is
    // about. That window is when CopilotKit dispatches its own resume run after
    // `respond()`, and a keypress landing in it used to take the send branch and
    // call `runAgent` again: two runs on one threadId, the resume cut off
    // mid-stream by `detachActiveRun`. Same double-invocation the
    // refusal above exists to prevent, reached by a different door.
    if (isLoading || agent?.isRunning) {
      if (pendingQueue.length >= PENDING_QUEUE_MAX) {
        showQueueToast(`待发送队列最多 ${PENDING_QUEUE_MAX} 条`);
        return;
      }
      setPendingQueue(prev => [
        ...prev,
        { id: crypto.randomUUID(), text: sendText, createdAt: Date.now() },
      ]);
      if (!text) setInputText('');
      if (textareaRef.current) textareaRef.current.style.height = '';
      return;
    }

    // Queue drain sends text-only — never auto-attach files the user
    // happened to add after enqueue. Snapshotting attachments at enqueue
    // time would be the alternative; keeping queued sends file-less is
    // simpler and matches the spec's "queue holds text only" model.
    const doneFiles = opts?.fromQueue
      ? []
      : pendingFiles.filter(pf => pf.status === 'done' && pf.uploaded);

    const thisGeneration = ++sendGenerationRef.current;

    const fileLabel = doneFiles.length > 0
      ? '\n\n📎 ' + doneFiles.map(pf => pf.file.name).join(', ')
      : '';
    let contextFileName: string | undefined;
    if (selectedFile && contextFileEnabled && !contextFileLabelShownRef.current) {
      contextFileName = selectedFile.name;
      contextFileLabelShownRef.current = true;
    }
    let assetScopeName: string | undefined;
    if (assetScope && assetScopeLabelShownForRef.current !== assetScope.assetId) {
      assetScopeName = assetScope.assetName;
      assetScopeLabelShownForRef.current = assetScope.assetId;
    }
    // UC parallel: show the "KB: <ucName>" tag once on the first message after a
    // (re)bind. asset/UC are mutually exclusive, so at most one of the two shows.
    let ucScopeName: string | undefined;
    if (ucScope && ucScopeLabelShownForRef.current !== String(ucScope.solutionMapId)) {
      ucScopeName = ucScope.ucName;
      ucScopeLabelShownForRef.current = String(ucScope.solutionMapId);
    }
    // Share one id between the DisplayMessage and the AG-UI agent message so
    // the mirror effect can match them and preserve display-only fields
    // (fileLabel-appended content, contextFileName, assetScopeName, ucScopeName)
    // when it rebuilds `messages` from agent.messages.
    const userMsgId = `user-${thisGeneration}-${crypto.randomUUID()}`;
    // The message itself goes to `agent.addMessage` below; only the display-only
    // chips are recorded here, under the same id, so the bubble can pick them up.
    // `fileLabel` is among them because the agent-side content must stay exactly
    // what the user typed — the 📎 line is a rendering of the attachments, not
    // part of the prompt.
    if (fileLabel || contextFileName || assetScopeName || ucScopeName) {
      setExtras(prev => new Map(prev).set(userMsgId, {
        ...(fileLabel ? { contentSuffix: fileLabel } : {}),
        contextFileName,
        assetScopeName,
        ucScopeName,
      }));
    }
    setInputText('');
    // Clear attached files immediately on send (files disappear from input area).
    // Skip when draining the queue — queued sends are text-only, so the
    // user's currently-attached files (added after enqueue) must survive.
    if (!opts?.fromQueue) setPendingFiles([]);
    setIsLoading(true);
    setHasChatted(true);
    userScrolledUpRef.current = false;
    setUserScrolledUp(false);

    // Reset per-turn state
    conversationIdRef.current = '';
    runtimeConfirmedRef.current = false;

    if (textareaRef.current) {
      textareaRef.current.style.height = '';
    }

    // AG-UI SEAM #2: send path.
    // Legacy: this position ran a fetch POST /events + SSE parsing pipeline
    // and committed the final message here. AG-UI just hands the message to
    // CopilotKit; the sync effect above mirrors streaming state into
    // messages / streamingText / isLoading.
    //
    // Parity with legacy body: media / context.items / report_metadata /
    // sender ride on `forwardedProps` — AG-UI's `any` pass-through slot
    // (@ag-ui/core RunAgentInput.forwardedProps). The runtime already reads
    // `forwardedProps.protocol` on the cancel path (see handleStop below),
    // so extra keys under the same bag stay on one classifier. Shape mirrors
    // the legacy `POST /events` body (minus sessionId/messages/agent which
    // AG-UI carries on protocol channels) so the runtime handler can share
    // one read path.
    const allMedia: UploadedFile[] = doneFiles.map(pf => pf.uploaded!);
    if (selectedFile && contextFileEnabled) {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase() || '';
      const mimeMap: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
        webp: 'image/webp', svg: 'image/svg+xml', pdf: 'application/pdf',
        csv: 'text/csv', json: 'application/json', md: 'text/markdown',
        html: 'text/html', txt: 'text/plain', xml: 'application/xml',
      };
      const mimetype = mimeMap[ext] || 'application/octet-stream';
      const type = mimetype.startsWith('image/') ? 'image' : 'document';
      allMedia.push({
        file_id: `ws-${selectedFile.key}`,
        filename: selectedFile.name,
        mimetype, type, format: ext,
        size: selectedFile.size,
        s3_key: `${uid || 'anonymous'}/rocky/output/${selectedFile.key}`,
        s3_uri: '', s3_bucket: '',  // gateway fills these
      });
    }

    // context.items — asset/uc are mutually exclusive, at most one item.
    // (AgUiChatPanel doesn't wire storylineScope; the legacy 3rd branch is
    // intentionally absent here.) Runtime renders items into an
    // <application_context> block as data, never identity/authz.
    const forwardedContext: Record<string, unknown> = {
      login: uid || 'anonymous',
      source: 'rocky',
    };
    if (assetScope) {
      forwardedContext.items = [{
        description: '用户当前聚焦的 Asset',
        value: `${assetScope.assetName} (id: ${assetScope.assetId})`,
      }];
    } else if (ucScope) {
      forwardedContext.items = [{
        description: '用户当前聚焦的 Use Case',
        value: `${ucScope.ucName} (solutionMapId: ${ucScope.solutionMapId})`,
      }];
    }
    if (metadata) {
      forwardedContext.report_metadata = JSON.stringify({
        data_window: metadata.data_window,
        kpi: metadata.kpi,
        highlights: metadata.highlights,
        suggested_questions: metadata.suggested_questions,
        template: metadata.template,
        sub_segments: metadata.sub_segments,
      });
    }

    agent.addMessage({
      id: userMsgId,
      role: 'user',
      content: sendText,
    });

    try {
      await copilotkit.runAgent({
        agent,
        forwardedProps: {
          protocol: 'ag-ui',
          context: forwardedContext,
          ...(allMedia.length > 0 ? { media: allMedia } : {}),
          ...(senderInfo ? { sender: senderInfo } : {}),
          // Applies from this message on; absent = the agent's default model.
          ...(modelChoice.requestModel ? { model: modelChoice.requestModel } : {}),
        },
      });
    } catch (err) {
      console.error('[AgUiChatPanel] runAgent failed:', err);
      // If runAgent rejects before agent.isRunning ever flips true (transport
      // /auth error before the WS opens), the mirror effect never re-fires,
      // so isLoading stays true and the "Thinking…" indicator sticks forever.
      // Clear it here and surface a chat-stream error so the user knows the
      // send failed and can retry. Guard with the generation ref so a
      // concurrent Stop / New Session doesn't get their state clobbered.
      //
      // The error must be added to agent.messages (not just local `messages`)
      // — otherwise the next mirror pass rebuilds `messages` from
      // agent.messages and silently drops the bubble.
      if (sendGenerationRef.current === thisGeneration) {
        setIsLoading(false);
        try {
          agent.addMessage({
            id: `error-${crypto.randomUUID()}`,
            role: 'assistant',
            content: tChat('errors.sendFailed'),
          });
        } catch (addErr) {
          // There is no local list to fall back to any more — the transcript IS
          // `agent.messages`. If the SDK refuses the message there is nothing to
          // show, so at least make the failure findable.
          // eslint-disable-next-line no-console
          console.error('[AgUiChatPanel] could not surface send failure', addErr);
        }
      }
    }

    if (sendGenerationRef.current !== thisGeneration) return;

    // A file written this turn is new to the workspace tree; refresh it, as
    // ChatPanelLegacy does after a file_output.
    if (fileSentSince(agent.messages ?? [], userMsgId)) refreshWorkspace();
  }, [inputText, isLoading, isUploading, pendingFiles, pendingQueue.length, showQueueToast, sessionId, uid, selectedFile, contextFileEnabled, metadata, senderInfo, assetScope, ucScope, agent, copilotkit, refreshWorkspace, cancelPendingApproval, modelChoice.requestModel, tChat]);

  // ── Pending queue helpers ─────────────────────────────────────────
  const handleCancelQueued = useCallback((id: string) => {
    setPendingQueue(prev => prev.filter(q => q.id !== id));
  }, []);

  const handleSendQueuedNow = useCallback((id: string) => {
    setPendingQueue(prev => {
      const target = prev.find(q => q.id === id);
      if (!target) return prev;
      const others = prev.filter(q => q.id !== id);
      return [target, ...others];
    });
    // If currently streaming, stop — drain effect will pick up the head.
    // If not streaming (race: response just ended), drain effect fires
    // immediately on next state commit.
    if (isLoading) {
      // handleStop is declared below; safe at runtime via closure.
      handleStopRef.current?.();
    }
  }, [isLoading]);

  // Forward ref so handleSendQueuedNow can call handleStop without
  // a circular useCallback dependency.
  const handleStopRef = useRef<(() => void) | null>(null);

  // Drain: when stream ends and queue is non-empty, send the head.
  useEffect(() => {
    // An approval card means a run is paused on a human, and `isLoading` is FALSE
    // during that pause — so without this guard the drain fires, re-enters
    // handleSend below the enqueue branch, and dismisses a card the operator has
    // not even read. Hold the queue until they answer it.
    // A recovered card is the same pause seen from the other side of a reload, and
    // a drain would send a message typed before that card existed.
    //
    // `gateAwaitingAnswerRef` — derived from `agent.messages` — rather than the
    // mounted-card registry: a collapsed panel or an open history drawer unmounts
    // every card and empties that registry while the gate is still unanswered, and
    // the drain would then pop the queue head only for `handleSend` to refuse it.
    if (gateAwaitingAnswerRef.current || recoveredGatesRef.current.length > 0) return;
    // `isRunning` as well as `isLoading`, for the same reason the enqueue branch
    // reads both: a CopilotKit-driven resume run never sets `isLoading`, so a
    // drain here would start a second run alongside it. `agent?.isRunning` is in
    // the dependency list so the drain is re-evaluated on the render that
    // observes the run ending.
    if (isLoading || agent?.isRunning || isUploading) return;
    if (pendingQueue.length === 0) return;
    if (drainRef.current) return;

    drainRef.current = true;
    const [next, ...rest] = pendingQueue;
    setPendingQueue(rest);

    // Snapshot the session at schedule time. If handleNewSession / handleLoadSession
    // rotates sessionId (bumping sendGenerationRef and clearing the queue) before
    // this microtask fires, the queued send belongs to a session that no longer
    // exists — dropping it prevents it from leaking into the new session under
    // the freshly-rotated threadId.
    const scheduledSessionId = sessionId;
    const scheduledGen = sendGenerationRef.current;
    // Defer one microtask so React commits the setPendingQueue above
    // before handleSend flips isLoading back on. Without this, the
    // effect re-runs in the same batch and could double-fire.
    Promise.resolve().then(() => {
      drainRef.current = false;
      if (sendGenerationRef.current !== scheduledGen) return;
      if (sessionId !== scheduledSessionId) return;
      handleSend(next.text, { fromQueue: true });
    });
  }, [isLoading, agent?.isRunning, isUploading, pendingQueue, handleSend, sessionId]);

  // ── Keyboard ──────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();  // handleSend is the single gatekeeper for send / enqueue / email-fallback
    }
  }, [handleSend]);

  // Auto-send prompt from context (e.g. WorkspaceHome card clicks).
  // While streaming, handleSend's enqueue branch picks it up; the cap
  // toast still fires when the queue is full.
  //
  // Keyed on the prompt only, with handleSend read through a ref. handleSend is
  // rebuilt when the send itself flips isLoading, and the panel re-renders with
  // that before the provider's clearPendingPrompt has landed — so an effect that
  // also depended on handleSend ran a second time with the same prompt, found the
  // turn in flight and queued it, and the drain asked the same question again once
  // the first answer ended. Typed sends never came through here, which is why only
  // workspace cards doubled.
  const handleSendRef = useRef(handleSend);
  handleSendRef.current = handleSend;
  useEffect(() => {
    if (!pendingPrompt) return;
    clearPendingPrompt();
    handleSendRef.current(pendingPrompt);
  }, [pendingPrompt, clearPendingPrompt]);

  // Prefill input from context without auto-sending (user decides when to send).
  // If streaming is in progress, discard the draft.
  useEffect(() => {
    if (!draftPrompt) return;
    clearDraftPrompt();
    if (!isLoading) {
      setInputText(draftPrompt);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [draftPrompt, clearDraftPrompt, isLoading]);

  // ── Stop streaming ────────────────────────────────────────────────

  const handleStop = useCallback(() => {
    // AG-UI SEAM #4: stop path.
    //
    // Two things need to happen:
    //   (1) Stop the client-side subscription so no more deltas render.
    //       agent.abortRun() (from @ag-ui/client's AbstractAgent) detaches
    //       the active run and flips agent.isRunning to false. Our SEAM #1
    //       sync effect then commits the partial assistant message and
    //       clears streamingText/Thought on the next render.
    //   (2) Tell the Gateway to kill the server-side agent process so it
    //       doesn't keep burning tokens. Legacy uses POST /channels/rocky/stop
    //       and the same endpoint applies here — the runtime hook keys on
    //       {session_id, conversation_id} and cancels the underlying run.
    //       For AG-UI we use agent.threadId as the conversation id (the
    //       runtime treats them interchangeably); if the endpoint doesn't
    //       recognize it, this is fire-and-forget so nothing breaks.
    //
    // sendGenerationRef bump invalidates any in-flight handleSend promise.
    sendGenerationRef.current++;
    // Reject any pending HITL approval — the user just stopped the run, so the
    // ApprovalPanel is now orphaned. Without this, the Promise leaks and the
    // panel keeps rendering with a resolve pointing at an aborted tool call.
    cancelPendingApproval();
    // A recovered card goes too, but its run is still parked server-side, so this
    // also re-arms the recovery query rather than hiding it for good.
    dropRecoveredGates();
    if (agent.isRunning) {
      try {
        agent.abortRun();
      } catch (err) {
        console.warn('[AgUiChatPanel] abortRun failed:', err);
      }
    }

    if (sessionId) {
      const headers = gatewayAuthHeaders({ 'Content-Type': 'application/json' });
      const stopBody: Record<string, unknown> = {
        session_id: sessionId,
        // Mirror the send-path convention: AG-UI clients mark protocol via
        // ``forwardedProps.protocol``. Runtime's ``is_agui_request(payload)``
        // reads exactly this shape, so both send and cancel share one
        // classifier — a future protocol bump only needs to update that
        // one function.
        forwardedProps: { protocol: 'ag-ui' },
      };
      // AG-UI cancel key: threadId is the runtime handler's PRIMARY match
      // (CopilotKit's per-thread StrandsAgentCore — the object holding
      // Strands' _cancel_signal — is stored under this key). Send it
      // unconditionally on AG-UI stops.
      if (agent?.threadId) stopBody.threadId = agent.threadId;
      // conversation_id is the SECONDARY key — captured from the CUSTOM
      // 'session_metadata' event emitted after RUN_STARTED. Legacy runtime
      // paths and some hook probes still match on this; harmless to include
      // even when threadId is present.
      const convId = conversationIdRef.current;
      if (convId) stopBody.conversation_id = convId;
      fetch(`${appConfig.gatewayUrl}/channels/rocky/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify(stopBody),
      }).catch(() => {
        // Best-effort; if the runtime doesn't accept this shape we still
        // stopped locally via abortRun above.
      });
    }
  }, [agent, sessionId, cancelPendingApproval, dropRecoveredGates]);

  // Expose handleStop to handleSendQueuedNow without a circular useCallback dep.
  useEffect(() => {
    handleStopRef.current = handleStop;
  }, [handleStop]);

  // ── Leave the in-flight run without cancelling it ─────────────────
  //
  // Switching threads (history load / new session) deliberately does NOT cancel
  // the run: it keeps going server-side and persists into its own thread, where
  // the user finds it when they come back. What a switch must do is stop THIS
  // page from applying the run's remaining events. There is one agent instance
  // for every thread, and @ag-ui/client's reducer writes each event's result
  // straight into `agent.messages` with no thread check — a frame landing after
  // the switch replaced the newly opened thread's transcript with the old one.
  //
  // `agent.abortRun()` alone does not do it: ProxiedCopilotRuntimeAgent overrides
  // it to POST `agent/stop` (which the Gateway rejects — it is not a cancel) and
  // never aborts the fetch. Its CopilotKit wrapper does abort the run controller,
  // which is still needed: no follow-up run (it would start on the NEW threadId)
  // and the run's trailing RUN_ERROR is not reported. `detachActiveRun()` is what
  // ends the stream: it completes the event pipeline, closing the SSE body (the
  // Runtime notices the dead socket and keeps draining the agent to completion),
  // and resolves once the run has finalized — so nothing is left in flight when
  // the caller switches.
  //
  // Bounded by DETACH_TIMEOUT_MS so a stuck finalize cannot wedge the UI. Giving
  // up there must not leave the run attached, or its late frames overwrite the
  // thread we switch to — the very bug this exists for. So a timeout forces the
  // transport shut: HttpAgent's own `abortController`, the one its overridden
  // `abortRun` never reaches. @ag-ui/client turns the resulting AbortError into a
  // `RUN_ERROR` with code "abort" and completes the stream, which CopilotKit does
  // not report. A short grace then lets the run finalize before the switch.
  const detachInFlightRun = useCallback(async () => {
    if (!agent.isRunning) return;
    try { agent.abortRun(); } catch { /* best-effort */ }
    if (typeof agent.detachActiveRun !== 'function') return;
    const detached = agent.detachActiveRun();
    detached.catch((err: unknown) => {
      console.warn('[AgUiChatPanel] detachActiveRun failed:', err);
    });
    if (await settlesWithin(detached, DETACH_TIMEOUT_MS)) return;
    console.warn(
      '[AgUiChatPanel] run did not detach within %dms; aborting its transport',
      DETACH_TIMEOUT_MS,
    );
    try {
      (agent as { abortController?: AbortController }).abortController?.abort();
    } catch { /* best-effort */ }
    await settlesWithin(detached, DETACH_ABORT_GRACE_MS);
  }, [agent]);

  /**
   * Run *apply* once no run is in flight. Synchronous when idle (the common case,
   * and the mount-time bootstrap); otherwise after `detachInFlightRun`, and only
   * if no newer switch happened in the meantime.
   */
  const switchThreadWhenDetached = useCallback((apply: () => void) => {
    const gen = ++switchGenRef.current;
    if (!agent.isRunning) {
      apply();
      return;
    }
    void detachInFlightRun().then(() => {
      if (gen === switchGenRef.current) apply();
    });
  }, [agent, detachInFlightRun]);

  // ── New session ───────────────────────────────────────────────────

  // The body of a new-session switch; runs with no run in flight.
  const startNewSession = useCallback(() => {
    sendGenerationRef.current++;
    // Clear the send-lock here rather than relying on onRunFinalized, so the new
    // (empty) session never ships with input/upload/mode-switch stuck
    // disabled={isLoading}. Mirrors loadSessionNow.
    setIsLoading(false);
    // AG-UI: the in-flight run (if any) was detached by switchThreadWhenDetached
    // — left running server-side, no longer applied here — so wiping agent state
    // below cannot be undone by a late frame.
    // Reject any pending HITL approval before rotating the session — otherwise
    // the ApprovalPanel keeps rendering with a resolve pointing at an aborted
    // tool call, and the Promise is retained forever.
    cancelPendingApproval();
    dropRecoveredGates();
    agent.setMessages([]);
    // Reset conversation id — otherwise a Stop clicked before the new run
    // emits session_metadata would target the previous session's conversation.
    conversationIdRef.current = '';
    runtimeConfirmedRef.current = false;
    // Rotate agent.threadId so the runtime sees a new session_id (persisted
    // messages key on this). Local sessionId mirrors it so uploads/stops
    // target the same DDB partition. Prefixed with "rocky-" for visual
    // parity with the initial mount value.
    const newThreadId = `rocky-${crypto.randomUUID()}`;
    agent.threadId = newThreadId;
    setSessionId(newThreadId);
    // Pre-mark this fresh thread as already bootstrapped so the bootstrap
    // effect (which fires on sessionId change) doesn't send a listSession-
    // Messages request for a session we KNOW has no history yet. Without
    // this, every "New Session" click round-trips an empty fetch.
    if (userKey) {
      bootstrappedForRef.current = `${userKey}::${newThreadId}`;
    }
    // `agent.setMessages([])` above already emptied the transcript. The welcome
    // bubble is rendered for that empty state rather than injected as a message —
    // injecting it meant every consumer of `agent.messages` had to know to ignore
    // a message the agent never produced.
    setExtras(new Map());
    setInputText('');
    setHasChatted(false);
    setShowDefaultSuggestions(true);
    setPendingQueue([]);
    messagesNextTokenRef.current = undefined;
    historySessionIdRef.current = undefined;
    olderMsgGenRef.current++;
    // A fresh thread has no history to replay. Left populated, a settled-gate
    // reply still in flight for the PREVIOUS session would rebuild its transcript
    // into the new one — so the generation bump and the cleared inputs are two
    // independent reasons that cannot happen.
    replayRowsRef.current = [];
    replayGatesRef.current = [];
    replayHistoryCompleteRef.current = false;
    replayTailTrimmedRef.current = false;
    settledGatesGenRef.current++;
  }, [agent, userKey, cancelPendingApproval, dropRecoveredGates]);

  const handleNewSession = useCallback(() => {
    // Legacy parity: prompt only during the pre-first-payload window (send
    // submitted but no delta/tool_start yet). Once the runtime has produced
    // content, the switch is silent — matches ChatPanelLegacy's
    // runtimeConfirmedRef gate.
    if (isLoading && !runtimeConfirmedRef.current) {
      const confirmed = window.confirm(tChat('session.switchConfirm'));
      if (!confirmed) return;
    }
    switchThreadWhenDetached(startNewSession);
  }, [isLoading, switchThreadWhenDetached, startNewSession, tChat]);

  // ── Reset chat when impersonation user changes ──────────────────
  const prevEffectiveUidRef = useRef(effectiveUid);
  useEffect(() => {
    if (prevEffectiveUidRef.current !== effectiveUid) {
      prevEffectiveUidRef.current = effectiveUid;
      handleNewSession();
    }
  }, [effectiveUid, handleNewSession]);

  // ── Resume incomplete session ───────────────────────────────────
  // Legacy path used to fetch POST /sessions/resume + SSE parsing to catch up
  // on an in-flight agent turn after a reload. CopilotKit owns its own session,
  // so there's nothing for the client to resume — this is a no-op kept for
  // signature parity with ChatPanelLegacy. Wire real rehydration here if
  // AG-UI ever adds it.
  const resumeSession = useCallback(async (_sid: string, _conversationId?: string) => {
    /* no-op */
  }, []);

  /**
   * Rebuild `agent.messages` from the current replay inputs, preserving anything
   * live that sits after the replayed history.
   *
   * Every contributor calls this instead of `setMessages` with its own snapshot,
   * so the last one to arrive adds to the transcript rather than replacing it.
   *
   * The live tail is identified by ID, not by a counter. Replay ids are
   * deterministic (`message_id`, `-call`, `-result`), so anything in
   * `agent.messages` that is not one of them was produced by this page — the
   * user's own message, a streaming reply, a tool result CopilotKit spliced in —
   * and is kept in order after the history. A generation counter cannot do this:
   * it only tells us that something happened, and every new writer to
   * `agent.messages` would have to remember to bump it.
   *
   * Live rows that somehow precede the last replayed message ARE dropped, and
   * logged — that ordering should be impossible (replayed history is older than
   * anything this page produced), so it means an assumption here is wrong and the
   * console should say so rather than the transcript quietly reordering itself.
   */
  const applyReplay = useCallback(() => {
    if (!agent) return;
    const desired = buildReplayMessages(replayRowsRef.current, replayGatesRef.current, {
      historyIsComplete: replayHistoryCompleteRef.current,
    });
    const replayIds = new Set(desired.map(m => m.id));
    const current = (agent.messages ?? []) as Array<{ id: string }>;
    let lastReplayIdx = -1;
    current.forEach((m, i) => { if (replayIds.has(m.id)) lastReplayIdx = i; });
    const after = current.slice(lastReplayIdx + 1);
    const liveTail = after.filter(m => !replayIds.has(m.id));
    const droppedBefore = current
      .slice(0, lastReplayIdx + 1)
      .filter(m => !replayIds.has(m.id));
    if (droppedBefore.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[AgUiChatPanel] replay dropped %d live message(s) ordered before replayed history',
        droppedBefore.length,
      );
    }
    agent.setMessages([...desired, ...liveTail] as typeof agent.messages);
  }, [agent]);

  // ── Load historical session ─────────────────────────────────────
  // The body of a history load; runs with no run in flight (see handleLoadSession).
  const loadSessionNow = useCallback((historicalSessionId: string, msgs: SessionMessage[], nextToken?: string) => {
    // Read before `agent.threadId` is reassigned below.
    const switchingThread = historicalSessionId !== agent.threadId;

    // Reset current session state
    sendGenerationRef.current++;
    setIsLoading(false);
    setInputText('');
    setHasChatted(true);
    setPendingQueue([]);

    // Store pagination state for older messages
    olderMsgGenRef.current++;
    messagesNextTokenRef.current = nextToken;
    historySessionIdRef.current = historicalSessionId;

    // AG-UI: point the agent at the historical thread. The runtime keys
    // persisted messages by session_id (= threadId), so the next turn must
    // hit the same partition or it splits into a new session. Local
    // sessionId mirrors it for /upload and /stop.
    //
    // A run in flight on the thread we are leaving was detached (not cancelled)
    // by handleLoadSession before this ran — see detachInFlightRun.
    // Reject any pending HITL approval — loading a different session orphans
    // the current one; leaving it in state would render a phantom panel over
    // the loaded history.
    cancelPendingApproval();
    // Recovered cards belong to a THREAD, so they only go when the thread does.
    // The mount-time history bootstrap calls this for the current thread, racing
    // the recovery query on exactly the reload the cards exist for — dropping them
    // here on that path stranded a parked run with no card and no re-query.
    if (switchingThread) {
      dropRecoveredGates();
      // Release the latch: it may still name this thread from an earlier visit,
      // which would make the query below a no-op on the way back.
      recoveryCheckedThreadRef.current = null;
    }
    // Reset conversation id so a Stop clicked before the loaded session's
    // next turn doesn't cancel the previous conversation.
    conversationIdRef.current = '';
    runtimeConfirmedRef.current = false;
    agent.threadId = historicalSessionId;
    setSessionId(historicalSessionId);
    // A thread opened from history can be parked on a gate whose card this page
    // never had (or lost when the operator left it). A parked gate is not in
    // DynamoDB as a tool result and has no verdict for /gate-history, so only the
    // Runtime can say it is waiting — the same question a reload asks. Without it,
    // the card came back only after a refresh. Same-thread loads (the mount
    // bootstrap) are already covered by the mount effect.
    if (switchingThread) {
      sessionIdRef.current = historicalSessionId;
      void checkPendingInterruptsRef.current?.(historicalSessionId);
    }

    // Rebuild historicalThoughts map so later live syncs (which read from
    // agent.messages, where tool_use rows don't exist) can resolve the
    // thought string for each historical assistant message by id.

    // Detect if the last agent round is incomplete (no agent_final_response after last user_query)
    const lastUserIdx = msgs.map(m => m.message_type).lastIndexOf('user_query');
    const hasAgentFinalAfterLastUser = lastUserIdx >= 0 &&
      msgs.slice(lastUserIdx + 1).some(m => m.message_type === 'agent_final_response');

    // Also check for stale sessions (> 8 hours since last message)
    const lastMsg = msgs[msgs.length - 1];
    const isStale = lastMsg && (Date.now() - new Date(lastMsg.create_time).getTime() > 8 * 60 * 60 * 1000);

    const needsResume = lastUserIdx >= 0 && !hasAgentFinalAfterLastUser && !isStale;

    // If incomplete, only render messages up to (and including) the last user_query
    const msgsToRender = needsResume ? msgs.slice(0, lastUserIdx + 1) : msgs;

    setExtras(sessionMessagesToExtras(msgsToRender));

    // AG-UI: replay history into agent.messages so the next runAgent()
    // sends the full conversation to the runtime. Without this, the LLM
    // only sees the new user turn and has no context — the DDB rows
    // exist but never round-trip back to Strands.
    //
    // It is also what the RENDERER reads: every tool call, gate or not, is now
    // rendered from `agent.messages` by a CopilotKit renderer, so a tool_use row
    // missing here is a trace row or a card that vanishes on refresh. That is why
    // `buildReplayMessages` synthesizes all of them. Gate rows are the exception
    // and need a second pass below — they cannot be synthesized until their
    // verdicts arrive, because a gate's arguments and result come from the
    // Runtime, not from DynamoDB.
    //
    // The extra tool messages do not reach the runtime: `messageWindowMiddleware`
    // keeps only from the latest user message onward, and replayed history is by
    // definition before it.
    replayRowsRef.current = msgsToRender;
    replayGatesRef.current = [];
    // Complete only if there is no older page AND nothing was trimmed off the end:
    // `msgsToRender` drops an incomplete trailing turn, so neither end of this list
    // can be assumed to be the end of the conversation.
    replayTailTrimmedRef.current = needsResume;
    replayHistoryCompleteRef.current = !nextToken && !needsResume;
    // `applyReplay` keeps every message it does not recognise as a "live tail" —
    // right for the thread already on screen (bootstrap, gate pass, older pages
    // all add to it), wrong for a different thread: the one we are leaving would
    // be appended under this one's history. Start the new thread from empty.
    if (switchingThread) agent.setMessages([]);
    applyReplay();

    // Settled approval cards: fetch the verdicts and re-apply with the gate
    // tool calls spliced in. Deliberately a second pass rather than blocking the
    // history render on it — the transcript should appear immediately, and the
    // audit cards are additive. On any failure the transcript simply stays as
    // rendered above, which is the pre-existing behaviour.
    const gateGen = ++settledGatesGenRef.current;
    void (async () => {
      try {
        const resp = await gatewayClient.getGateHistory(historicalSessionId, agentName);
        // A newer session load happened while we waited — its rows are already in
        // `replayRowsRef`, and these verdicts belong to the session we left.
        // (A live turn is NOT this check's job: `applyReplay` keeps live messages
        // by id, so the two no longer compete.)
        if (gateGen !== settledGatesGenRef.current) return;
        if (resp.store !== 'agentcore_memory' || !resp.gates?.length) return;
        // One list to update, not two. Position used to be a separate decision
        // carried on the display projection, and keeping the two in step is what
        // broke twice; the cards now live in `agent.messages` alongside their
        // content. The extras are unchanged by verdicts, so they are not re-derived.
        replayGatesRef.current = resp.gates;
        applyReplay();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[AgUiChatPanel] failed to load settled gates for', historicalSessionId, err);
      }
    })();

    setShowHistory(false);

    // If the session is incomplete, we would normally resume SSE to catch up
    // on the agent's in-flight response. resumeSession is a no-op stub under
    // AG-UI (CopilotKit owns the run lifecycle), so setting isLoading=true
    // here would strand the "Thinking..." indicator forever. Fire the stub
    // for signature parity but keep isLoading=false so the user can continue.
    if (needsResume) {
      const lastUserMsg = msgs[lastUserIdx];
      void resumeSession(historicalSessionId, lastUserMsg.conversation_id);
    }
  }, [resumeSession, agent, cancelPendingApproval, dropRecoveredGates, agentName, applyReplay]);

  const handleLoadSession = useCallback((historicalSessionId: string, msgs: SessionMessage[], nextToken?: string) => {
    if (historicalSessionId !== agent.threadId) {
      switchThreadWhenDetached(() => loadSessionNow(historicalSessionId, msgs, nextToken));
      return;
    }
    // Same thread (the mount-time bootstrap): nothing to leave. A run in flight
    // here is this thread's own and stays attached — `applyReplay` keeps its
    // messages as the live tail. abortRun only stops CopilotKit's follow-up run,
    // as before. Guarded on isRunning — ProxiedCopilotRuntimeAgent.abortRun()
    // POSTs `agent/stop` unconditionally.
    switchGenRef.current++;
    if (agent.isRunning) {
      try { agent.abortRun(); } catch { /* best-effort */ }
    }
    loadSessionNow(historicalSessionId, msgs, nextToken);
  }, [agent, switchThreadWhenDetached, loadSessionNow]);

  // ── Bootstrap persisted history for the current threadId ─────────────
  //
  // On mount / after a refresh, our threadId comes back from localStorage
  // but `agent.messages` starts empty — CopilotKit v2's `useAgent` does not
  // auto-fetch persisted messages by threadId. Without this effect, the
  // user reloads the tab and the chat looks empty (server-side context is
  // still there — the LLM sees prior turns on the next send — but the UI
  // has nothing to render).
  //
  // Runs at most once per (userKey, sessionId) pair. Skipped when:
  //   • the user has explicitly loaded a different session via ChatHistory
  //     (`historySessionIdRef.current` is set) — that path did its own load
  //   • `agent.messages` is already non-empty — a live turn started before
  //     we got a chance, don't clobber
  //   • Gateway returns no messages for this threadId — brand-new session,
  //     leave the welcome + suggested-questions UI intact.
  //
  // Restores the whole transcript: plain user / assistant turns, the collapsed
  // tool trace, and — once the second `getGateHistory` pass inside
  // `handleLoadSession` lands — the settled approval cards, each in the turn that
  // produced it.
  //
  // Two subtle correctness rules:
  //   1. Gate on `agentIsReady` — `useAgent` returns a placeholder before
  //      the runtime `/info` handshake, then swaps `agent` reference to the
  //      real instance. If we start the fetch against the placeholder, the
  //      effect gets cleanup'd (cancelled=true) when the reference swaps
  //      and the second run bails on the `bootstrappedForRef` guard. Net
  //      result: request is sent, response is silently dropped, UI stays
  //      empty. Waiting for `isReady` avoids the wasted round trip.
  //   2. Set `bootstrappedForRef` AFTER `handleLoadSession` succeeds, not
  //      before. If the effect gets cancelled or the fetch throws, the
  //      next run should be able to retry — pre-marking would lock us out
  //      of the fetch permanently for this (userKey, sessionId).
  const bootstrappedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!agentIsReady) return;
    if (!sessionId || !userKey) return;
    if (historySessionIdRef.current !== undefined) return;
    const key = `${userKey}::${sessionId}`;
    if (bootstrappedForRef.current === key) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await gatewayClient.listSessionMessages(sessionId, {
          limit: 100,
          order: 'desc',
          ...(isImpersonating ? { user: effectiveUid } : {}),
        });
        if (cancelled) return;
        if (!resp?.messages?.length) return;
        // If the user started a live run before we finished fetching, or
        // switched to a different history session, back off — do not
        // clobber that state.
        if ((agent?.messages ?? []).length > 0) return;
        if (historySessionIdRef.current !== undefined) return;

        // Same shape ChatHistory → handleLoadSession expects: chronological
        // order (`order: 'desc'` + reverse), pass along next_token for
        // scroll-up pagination.
        handleLoadSession(sessionId, resp.messages.slice().reverse(), resp.next_token);
        // Only mark done AFTER success — a mid-flight cancellation (e.g.
        // agent reference swap) can rerun the effect, which is the point.
        bootstrappedForRef.current = key;
      } catch (err) {
        // Best-effort: on failure the user sees the empty welcome, which is
        // no worse than the pre-fix behavior.
        // eslint-disable-next-line no-console
        console.warn('[AgUiChatPanel] failed to bootstrap history for session', sessionId, err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    agentIsReady,
    sessionId,
    userKey,
    agent,
    isImpersonating,
    effectiveUid,
    handleLoadSession,
  ]);

  // ── Answer a HITL gate recovered after a reload ─────────────────────
  //
  // The live path lets CopilotKit settle a gate via the render prop's
  // `respond()`. That callback died with the previous page, so a recovered
  // gate is answered the way CopilotKit does it under the hood: append the
  // trailing `role:'tool'` message keyed by the gate's toolCallId, then run.
  // The Runtime matches that toolCallId against its server-side wait bridge
  // (persisted in AgentCore Memory, independent of what the client sends) and
  // resumes the parked run — so the preceding assistant tool-call message does
  // not need to be present in `agent.messages`. The history-bootstrap effect
  // above has already replayed the prior user/assistant turns, so the request
  // carries the non-empty user message the Gateway requires.
  const resumeRecoveredGate = useCallback(
    async (interrupt: PendingInterrupt, value: unknown, opts?: { refused?: boolean }) => {
      if (!agent) return;
      // Drop the card immediately so a double-click can't fire two resumes for
      // the same toolCallId.
      setRecoveredGates((prev) => prev.filter((g) => g.tool_call_id !== interrupt.tool_call_id));
      agent.addMessage({
        id: crypto.randomUUID(),
        role: 'tool',
        toolCallId: interrupt.tool_call_id,
        content: JSON.stringify(value),
        // A refusal is expressed by the PROTOCOL's own field, not only by prose in
        // the content. `ToolMessage.error` is optional in AG-UI
        // (`@ag-ui/core` — `error?: string`), and the Runtime adapter turns it into
        // `is_error=True` on the resume entry it synthesises, so the model sees a
        // failed tool call rather than a successful one whose text happens to say
        // "cancelled". `DISMISSAL_NOTE` keeps its own job: what to do next.
        //
        // The live (non-recovered) 取消 still reaches the model as a success —
        // CopilotKit builds that tool message from the handler's return value and
        // only sets `error` when the handler throws. Not worth throwing for.
        ...(opts?.refused ? { error: 'Approval declined by the operator.' } : {}),
      } as unknown as Parameters<typeof agent.addMessage>[0]);
      setIsLoading(true);
      try {
        await copilotkit.runAgent({
          agent,
          forwardedProps: {
            protocol: 'ag-ui',
            context: { login: uid || 'anonymous', source: 'rocky' },
            ...(senderInfo ? { sender: senderInfo } : {}),
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AgUiChatPanel] resume of recovered gate failed', interrupt.tool_call_id, err);
      } finally {
        setIsLoading(false);
      }
    },
    [agent, copilotkit, uid, senderInfo],
  );
  resumeRecoveredGateRef.current = resumeRecoveredGate;

  // ── Recover pending HITL gates after a reload ───────────────────────
  //
  // Ask the Runtime (read-only) whether the thread is parked on any approval gate
  // whose interactive card was lost with the tab. If so, surface standalone recovery
  // cards (the tool calls are not in `agent.messages` after a reload — a still-parked
  // gate has no verdict for /gate-history to return — so the normal per-message card
  // path can't rebuild them).
  //
  // A CALLABLE, not only an effect, and that is the point. Two things need to ask:
  // the mount, and any moment a card is taken off the screen WITHOUT being answered
  // (Stop / New Session), because the run is then still parked server-side. Relying
  // on the effect to re-run for the second case does not work — its dependencies
  // (`agentIsReady`, `sessionId`, `userKey`, `agentName`) do not change when the
  // operator clicks Stop, and the effect is additionally gated behind
  // `historySessionIdRef`, which the mount-time history bootstrap sets for the
  // current thread as soon as that thread has any persisted messages (a parked one
  // always does). Both earlier attempts at "leave it un-latched so it can ask again"
  // were therefore dead code, and their tests passed only because the fixture
  // returned no messages and the bootstrap exited early.
  //
  // `store` discipline: only trust an authoritative `agentcore_memory` read. On
  // `unavailable` (or any error) show nothing — never treat "could not read" as "no
  // pending gate", which would silently hide a real one. We show nothing rather than
  // a scary banner; the run is still parked server-side. And "show nothing" must not
  // become "decide nothing": a non-authoritative answer leaves the thread un-latched,
  // so the next caller asks again.
  const checkPendingInterrupts = useCallback(async (threadId: string) => {
    if (!threadId || recoveryCheckedThreadRef.current === threadId) return;
    try {
      const resp = await gatewayClient.getPendingInterrupts(threadId, agentName);
      // The operator switched threads while we waited: this answer is about a
      // conversation no longer on screen, and its card must not land on this one.
      // Not latched either, so coming back to that thread asks again.
      if (sessionIdRef.current !== threadId) return;
      if (resp.store !== 'agentcore_memory') return; // unavailable → stay un-latched
      recoveryCheckedThreadRef.current = threadId;
      if (!resp.pending?.length) return;
      // If a live gate is already showing (a run started before the query returned),
      // don't duplicate it as a recovery card.
      if (pendingGatesRef.current.size > 0) return;
      setRecoveredGates(resp.pending);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[AgUiChatPanel] pending-interrupts recovery query failed', threadId, err);
    }
  }, [agentName]);
  checkPendingInterruptsRef.current = checkPendingInterrupts;

  // Mount / thread change. Skipped once a session has been loaded from history —
  // `loadSessionNow` asks for the thread it opens itself, explicitly, so the answer
  // is about that thread and not whichever one the effect happened to see.
  useEffect(() => {
    if (!agentIsReady) return;
    if (!sessionId || !userKey) return;
    if (historySessionIdRef.current !== undefined) return;
    void checkPendingInterrupts(sessionId);
  }, [agentIsReady, sessionId, userKey, checkPendingInterrupts]);

  // ── Load older messages on scroll-to-top ──────────────────────────
  const loadingOlderRef = useRef(false);
  const olderMsgGenRef = useRef(0);

  const loadOlderMessages = useCallback(async () => {
    const token = messagesNextTokenRef.current;
    const sid = historySessionIdRef.current;
    if (!token || !sid || loadingOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlderMessages(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const gen = ++olderMsgGenRef.current;

    try {
      // Forward impersonation to keep parity with the initial-page load —
      // an admin viewing another user's history who scrolls up would
      // otherwise trigger a gateway 404 (session not owned by requester)
      // and the paginated fetch silently fails.
      const resp = await gatewayClient.listSessionMessages(sid, {
        limit: 100,
        order: 'desc',
        next_token: token,
        ...(isImpersonating ? { user: effectiveUid } : {}),
      });

      if (gen !== olderMsgGenRef.current) return;

      messagesNextTokenRef.current = resp.next_token;

      const olderMsgs = resp.messages.reverse();
      const olderExtras = sessionMessagesToExtras(olderMsgs);

      if (olderMsgs.length > 0) {
        // Prepend through the SAME path the initial page uses. The previous
        // version filtered to user_query / agent_final_response on the grounds
        // that tool_use rows are UI breadcrumbs the LLM does not need — true of
        // the LLM, but the breadcrumbs are now RENDERED from `agent.messages`, so
        // dropping them here meant every paginated turn silently lost its tool
        // trace. Two implementations of "history → messages" is exactly the
        // pattern that has bitten this feature repeatedly; there is one now.
        //
        // Older rows join `replayRowsRef` rather than being spliced onto
        // `agent.messages` directly, so this page gets its APPROVAL CARDS too:
        // `buildReplayMessages(olderMsgs, [])` had no verdicts to pair with and
        // therefore omitted every gate row, with no later pass to fill them in.
        // Going through the shared inputs also means exhausting `next_token`
        // upgrades `historyIsComplete`, which is what re-enables positional
        // pairing for a long conversation.
        replayRowsRef.current = [...olderMsgs, ...replayRowsRef.current];
        replayHistoryCompleteRef.current = !resp.next_token && !replayTailTrimmedRef.current;
        setExtras(prev => {
          const next = new Map(prev);
          for (const [id, value] of olderExtras) next.set(id, value);
          return next;
        });
        applyReplay();
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          }
        });
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlderMessages(false);
    }
  }, [isImpersonating, effectiveUid, applyReplay]);

  const handleMessagesScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop < 50 && messagesNextTokenRef.current && !loadingOlderRef.current) {
      loadOlderMessages();
    }
    const delta = target.scrollTop - lastScrollTopRef.current;
    lastScrollTopRef.current = target.scrollTop;
    // Ignore sub-pixel jitter (< 2px)
    if (Math.abs(delta) < 2) return;
    // Our own scrolls are always DOWNWARD — every programmatic write is
    // `scrollTop = scrollHeight`. So an upward delta inside the programmatic
    // window is the operator, and swallowing it (as this did unconditionally)
    // loses the one event that tells us to stop following the stream.
    if (isProgrammaticScrollRef.current && delta > 0) return;
    if (delta < 0 && !userScrolledUpRef.current) {
      userScrolledUpRef.current = true;
      setUserScrolledUp(true);
    } else if (delta > 0) {
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 30) {
        userScrolledUpRef.current = false;
        setUserScrolledUp(false);
      }
    }
  }, [loadOlderMessages]);

  const scrollToBottom = useCallback(() => {
    userScrolledUpRef.current = false;
    setUserScrolledUp(false);
    const container = messagesContainerRef.current;
    if (container) {
      isProgrammaticScrollRef.current = true;
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  }, []);

  const userInitial = (display_name || uid || 'U').charAt(0).toUpperCase();

  // ── Navigate to a file output in workspace ────────────────────────
  // The shared opener, as in the legacy panel: it exits fullscreen, collapses the
  // chat overlay below desktop, refuses users without workspace access, and
  // routes back to `/` when the panel sits beside another page (IVT, Event Hub),
  // where setting pendingNavigateKey alone did nothing.
  const handleFileOutputClick = openWorkspaceFile;
  openFileRef.current = handleFileOutputClick;
  sendReplyRef.current = (text: string) => { void handleSend(text); };

  // ── Collapsed state ────────────────────────────────────────────────

  if (isCollapsed) {
    return (
      <aside className="chat-panel chat-panel--collapsed" onClick={onToggle}>
        <button
          className="chat-panel__expand-btn"
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          title={tChat('header.open')}
          aria-label={tChat('header.open')}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H24V20H8L4 24V4Z" stroke="#424650" strokeWidth="2" strokeLinejoin="round" fill="none"/>
          </svg>
        </button>
      </aside>
    );
  }

  // Everything the transcript's slot components need. One object so the slots
  // stay presentational and the panel keeps owning the data. Not memoized: it
  // depends on `resolvedExtras`, which is rebuilt each render along with the
  // transcript itself, so a memo here would only look like it was saving work.
  // Which assistant message is being produced right now — computed here, where the
  // whole list is in hand, and passed down the context so a memoized slot cannot
  // miss the transition. See `streamingMessageId` in TranscriptSlots.
  // Bounded at the last user message, like `turnHasAssistantReply`. Unbounded, a
  // turn in flight marked the PREVIOUS turn's finished answer as streaming — its
  // avatar spun and its trace force-reopened — because with `[u1, a1, u2]` the last
  // assistant in the list is `a1`. Moving this onto the context is what exposed it:
  // the CopilotKit memo used to refuse that re-render, since `a1` is not last.
  const streamingMessageId = (() => {
    if (!isTurnInFlight) return undefined;
    for (let i = transcriptMessages.length - 1; i >= 0; i--) {
      const role = transcriptMessages[i].role;
      if (role === 'user') return undefined;
      if (role === 'assistant') return transcriptMessages[i].id;
    }
    return undefined;
  })();

  // An open approval card owns the conversation: the composer is locked until it is
  // answered. Typing used to dismiss the card AND send a user message, which is the
  // shape that cannot be resumed and persisted at the same time — see the refusal in
  // `handleSend`. A single typeable card instead takes the typed text as its answer.
  // Derived from `agent.messages` (plus recovered cards), so it holds even when no
  // card is mounted, e.g. while the history drawer is open.
  const gateOpen = gateAwaitingAnswer || recoveredGates.length > 0;
  // …except the one card that takes a typed answer (`pickTypeableGate`).
  const gateTakesTyping = pickTypeableGate(openGates, recoveredGates, isTurnInFlight) !== null;
  const gateBlocksInput = gateOpen && !gateTakesTyping;

  const transcriptCtx = {
    extras: resolvedExtras,
    splitToolCalls,
    userInitial,
    userLabel: display_name || uid || 'User',
    onFileClick: handleFileOutputClick,
    streamingMessageId,
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <OpenWorkspaceFileContext.Provider value={openFileViaRef}>
    <aside
      className={`chat-panel${chatFullscreen ? ' chat-panel--fullscreen' : ''}`}
      style={chatFullscreen ? undefined : { width: chatWidth, minWidth: 280, maxWidth: 700 }}
    >
      {/* Drag handle on left edge (hidden in fullscreen) */}
      {!chatFullscreen && <div className="chat-panel__resize-handle" onMouseDown={handleResizeStart} />}

      {/* Overlay during resize */}
      {isResizing && <div className="chat-panel__resize-overlay" />}

      {/* Header */}
      <div className="chat-panel__header">
        <button className="chat-panel__header-btn chat-panel__header-btn--lead" onClick={() => setShowHistory(h => !h)} title={showHistory ? tChat('header.backToChat') : tChat('header.history')} aria-label={showHistory ? tChat('header.backToChat') : tChat('header.history')}>
          {showHistory ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="#424650" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <img src="/icons/icon-chat-list.svg" alt="" />
          )}
        </button>
        <h2 className="chat-panel__title">{showHistory ? tChat('header.historyTitle') : tChat('header.title')}</h2>
        <div className="chat-panel__header-actions">
          <button className="chat-panel__header-btn" onClick={handleNewSession} title={tChat('header.newConversation')} aria-label={tChat('header.newConversation')}>
            <img src="/icons/icon-chat-add.svg" alt="" />
          </button>
          {!hideFullscreen && (
            <button className="chat-panel__header-btn" onClick={toggleFullscreen} title={chatFullscreen ? tChat('header.exitFullscreen') : tChat('header.enterFullscreen')} aria-label={chatFullscreen ? tChat('header.exitFullscreen') : tChat('header.enterFullscreen')}>
              <img src={chatFullscreen ? "/icons/chat-half-screen.svg" : "/icons/chat-full-screen.svg"} alt="" />
            </button>
          )}
          {onToggle && (
            <button className="chat-panel__header-btn" onClick={onToggle} title={tChat('header.close')} aria-label={tChat('header.close')}>
              <img src="/icons/icon-chat-close.svg" alt="" />
            </button>
          )}
        </div>
      </div>

      {/* Phones only — names the attached Workspace file, which the open chat hides. */}
      {!showHistory && (
        <ContextFileBanner
          fileName={selectedFile?.name}
          enabled={contextFileEnabled}
          onExclude={() => setContextFileEnabled(false)}
        />
      )}

      {/* Messages / History */}
      {showHistory ? (
        <ChatHistory
          onLoadSession={handleLoadSession}
          agentName={agentName}
        />
      ) : (
      <div className="chat-panel__messages-wrap">
        <div className="chat-panel__messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {loadingOlderMessages && (
            <div className="chat-panel__loading-older">{tChat('messages.loadingOlder')}</div>
          )}
          {/* The transcript. `CopilotChatMessageView` walks the messages once
              and dispatches per role; our slot components supply the whole DOM,
              so no CopilotKit stylesheet is involved. What it brings that a hand
              written `.map()` did not: id-deduplication with a dev warning (id
              uniqueness is a contract CopilotKit checks, which is why
              `buildReplayMessages` suffixes its synthesized ids), and
              virtualization past 50 messages.

              `cursor` is suppressed: it renders on any running turn, so it cannot
              express "only before anything has arrived" — the placeholder below
              does that instead. */}
          <TranscriptProvider value={transcriptCtx}>
            <CopilotChatMessageView
              className="chat-panel__transcript"
              messages={transcriptMessages as never}
              isRunning={isTurnInFlight}
              assistantMessage={RockyAssistantMessage as never}
              userMessage={RockyUserMessage as never}
              cursor={NoCursor as never}
            />
          </TranscriptProvider>

          {/* Welcome / empty state. Rendered rather than injected as a message,
              so nothing downstream has to ignore a message the agent never
              produced. */}
          {transcriptMessages.length === 0 && !isTurnInFlight && (
            <div className="chat-msg--ai">
              <div className="chat-msg__ai-avatar-wrap">
                <Avatar imgUrl="/icons/avatar-rocky.png" color="gen-ai" ariaLabel="Avatar of Rocky AI assistant" />
              </div>
              <div className="chat-msg__ai-content">
                <div className="chat-msg__bubble">
                  <div className="chat-msg__bubble-text">
                    <MarkdownRenderer
                      markdown={historySessionIdRef.current !== undefined
                        ? 'This conversation has no messages yet.'
                        : welcomeMessage}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* "Thinking..." before the first assistant row of a turn exists.
              Once it does, that message renders its own open trace and spinning
              avatar, so this is only the gap between send and first frame. */}
          {isTurnInFlight && !turnHasAssistantReply(transcriptMessages) && (
            <div className="chat-msg--ai">
              <div className="chat-msg__ai-avatar-wrap">
                <Avatar imgUrl="/icons/avatar-rocky.png" color="gen-ai" ariaLabel="Avatar of Rocky AI assistant" loading />
              </div>
              <div className="chat-msg__ai-content">
                <ToolTrace isStreaming>{null}</ToolTrace>
              </div>
            </div>
          )}

          {/* Recovered HITL gates — approval cards rebuilt after a page reload
              from the server-side interrupt state (see the recovery effect and
              GatewayClient.getPendingInterrupts).
              Suppressed per gate, by toolCallId, whenever CopilotKit already has
              that tool call: then the normal render path is showing a real card
              with a live `respond`, and this fallback would duplicate it. Keyed
              on the id rather than on "is any card unplaced" — the fallback card
              and the real one are the same gate or they are not, and the old
              proxy silently stopped covering the case where the live card has a
              message to sit in. */}
          {(() => {
            const orphans = recoveredGates.filter(g => !toolCallsById.has(g.tool_call_id));
            if (orphans.length === 0) return null;
            return (
              <div className="chat-msg--ai">
                <div className="chat-msg__ai-avatar-wrap">
                  <Avatar imgUrl="/icons/avatar-rocky.png" color="gen-ai" ariaLabel="Avatar of Rocky AI assistant" />
                </div>
                <div className="chat-msg__ai-content">
                  {orphans.map((interrupt) => (
                    <RecoveredGateCard
                      key={interrupt.tool_call_id}
                      interrupt={interrupt}
                      agentName={agentName}
                      onResume={resumeRecoveredGate}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Suggested questions — inside chat, below last AI message. */}
          {/* Gated on the transcript as well as `hasChatted`: the flag is a
              separate bit of local state that can be reset (file-selection
              change, remount) while `agent.messages` survives, and suggestions
              must never appear under an existing conversation. */}
          {!isLoading && !hasChatted && transcriptMessages.length === 0 && !isImpersonating && (() => {
            if (showDefaultSuggestions) {
              return <SuggestedQuestions questions={defaultSuggestedQuestions} onSelect={(q) => handleSend(q)} />;
            }
            const reportQuestions = metadata?.suggested_questions?.length ? metadata.suggested_questions : null;
            if (reportQuestions) {
              return <SuggestedQuestions questions={reportQuestions} onSelect={(q) => handleSend(q)} />;
            }
            return null;
          })()}

          <div ref={bottomRef} />
        </div>

        {/* Floating "scroll to latest" button — positioned over messages area */}
        {userScrolledUp && (
          <button
            className="chat-panel__scroll-to-bottom"
            onClick={scrollToBottom}
            title={tChat('messages.scrollToLatest')}
            aria-label={tChat('messages.scrollToLatest')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M8 13L3 8M8 13L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      )}

      {/* AI disclaimer, as in ChatPanel. A plain link: it opens a new tab anyway,
          and this panel does not otherwise depend on the router. */}
      <a
        className="chat-panel__disclaimer"
        href="/help/ai-responses"
        target="_blank"
        rel="noopener noreferrer"
      >
        {tChat('disclaimer')}
      </a>

      {/* Input area */}
      <div className="chat-panel__input-area">
        {queueToast && (
          <div className="chat-panel__queue-toast" role="status" aria-live="polite">
            {queueToast}
          </div>
        )}
        <PendingQueue
          items={pendingQueue}
          onCancel={handleCancelQueued}
          onSendNow={handleSendQueuedNow}
        />
        <div className="chat-panel__input-wrapper" style={inputHeight ? { height: inputHeight } : undefined} onClick={() => textareaRef.current?.focus()}>
          <div className="chat-panel__input-resize-handle" onMouseDown={handleInputResizeStart} />
          {/* Drag-and-drop overlay — only mounts while a file is being dragged
              over the window and uploads are allowed. Scoped to the input box
              (not the whole panel) so it never blocks normal clicks. Reuses the
              same processFiles path as the Attach Files picker. */}
          {areFilesDragging && !isLoading && !isUploading && !isImpersonating && (
            <div
              className="chat-panel__dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFileDrop(Array.from(e.dataTransfer.files));
              }}
            >
              <div className="chat-panel__dropzone-content">
                <svg className="chat-panel__dropzone-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 15V4M12 4L8 8M12 4l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 15v2.5A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="chat-panel__dropzone-text">{tChat('files.drop')}</span>
              </div>
            </div>
          )}
          {/* File attachment cards (Cloudscape-style) */}
          {pendingFiles.length > 0 && (
            <div className="chat-panel__file-cards">
              {pendingFiles.map(pf => (
                <div key={pf.id} className={`chat-panel__file-card ${pf.status === 'error' ? 'chat-panel__file-card--error' : ''}`}>
                  <div className="chat-panel__file-card-info">
                    <span className="chat-panel__file-card-name" title={pf.file.name}>
                      {pf.file.name}
                    </span>
                    <span className="chat-panel__file-card-meta">
                      {pf.file.size < 1024
                        ? `${pf.file.size} B`
                        : pf.file.size < 1048576
                          ? `${(pf.file.size / 1024).toFixed(1)} KB`
                          : `${(pf.file.size / 1048576).toFixed(1)} MB`}
                    </span>
                    <span className="chat-panel__file-card-meta">
                      {new Date(pf.file.lastModified).toISOString().slice(0, 19)}
                    </span>
                    {pf.status === 'uploading' && (
                      <span className="chat-panel__file-card-status">{tChat('files.uploading')}</span>
                    )}
                  </div>
                  <button
                    className="chat-panel__file-card-dismiss"
                    onClick={() => handleRemoveFile(pf.id)}
                    title={tChat('files.remove')}
                    aria-label={tChat('files.removeNamed', { name: pf.file.name })}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {isImpersonating && (
            <span className="chat-panel__lock-icon">
              <Icon name="lock-private" size="small" />
            </span>
          )}
          <textarea
            ref={textareaRef}
            className={`chat-panel__textarea ${isImpersonating ? 'chat-panel__textarea--locked' : ''}`}
            style={inputHeight ? { height: '100%', maxHeight: 'none' } : undefined}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isImpersonating ? tChat('input.readOnly') : gateBlocksInput ? tChat('input.gateBlocks') : gateTakesTyping ? tChat('input.gateReply') : isLoading ? tChat('input.willQueue') : tChat('input.placeholder')}
            disabled={isImpersonating || gateBlocksInput}
            rows={1}
          />
          <div className="chat-panel__input-actions">
            {(isLoading || gateOpen) && (
              <button
                /* Also while a gate blocks the composer: a parked run leaves
                   `isLoading` false, so without this the operator can have no card
                   button (an agent that sent `options: []`), no Stop and no composer
                   — only New Session, which abandons the thread and, with the
                   threadId in sessionStorage, orphans the parked run. */
                className="chat-panel__input-btn chat-panel__stop-btn"
                onClick={handleStop}
                title={pendingQueue.length > 0 ? tChat('input.stopKeepQueue') : tChat('input.stop')}
                aria-label={tChat('input.stop')}
              >
                <img src="/icons/stop-circle.svg" alt="" />
              </button>
            )}
            <button
              className="chat-panel__input-btn"
              onClick={() => handleSend()}
              disabled={!inputText.trim() || isUploading || isImpersonating || gateBlocksInput}
              // Labelled by `isTurnInFlight`, the same flag `handleSend` branches
              // on, so the button cannot say "Send message" and then enqueue.
              title={isTurnInFlight ? 'Add to pending queue' : 'Send message'}
              aria-label={isTurnInFlight ? 'Add to pending queue' : 'Send message'}
            >
              <img src="/icons/icon-chat-send.svg" alt="" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="chat-panel__toolbar">
          <div className="chat-panel__toolbar-left">
            {/* Rocky's Pack — rocky's fixed three-slot row. */}
            {isRocky && (
            <div className="chat-panel__toolbar-pack">
              {groupedSkills.has('DataRetriever') ? (
                <SkillCard
                  category="DataRetriever"
                  skills={groupedSkills.get('DataRetriever')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/avatar-pack-dog.png" width={26} color="default" ariaLabel="DataRetriever" />
                </span>
              )}
              {groupedSkills.has('Hedwig') ? (
                <SkillCard
                  category="Hedwig"
                  skills={groupedSkills.get('Hedwig')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/avatar-pack-owl.png" width={26} color="default" ariaLabel="Hedwig" />
                </span>
              )}
              {groupedSkills.has('Cheetah') ? (
                <SkillCard
                  category="Cheetah"
                  skills={groupedSkills.get('Cheetah')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/cheetah.png" width={26} color="default" ariaLabel="Cheetah" />
                </span>
              )}
              <span className="chat-panel__toolbar-label">{tChat('pack.label')}</span>
            </div>
            )}
            {/* Other agents: a card per category they have skills in. */}
            {packCategories.length > 0 && (
            <div className="chat-panel__toolbar-pack">
              {packCategories.map((c) => (
                <SkillCard
                  key={c.label}
                  category={c.label}
                  skills={groupedSkills.get(c.label)!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ))}
              <span className="chat-panel__toolbar-label">{tChat('pack.agentLabel')}</span>
            </div>
            )}
            {selectedFile && (
              <>
                {/* Leading divider only when something precedes this chip. */}
                {showPack && <div className="chat-panel__toolbar-divider" />}
                <button
                  className={`chat-panel__toolbar-context-file ${!contextFileEnabled ? 'chat-panel__toolbar-context-file--disabled' : ''}`}
                  onClick={() => setContextFileEnabled(prev => !prev)}
                  title={contextFileEnabled ? `${selectedFile.name} (click to exclude from context)` : `${selectedFile.name} (click to include in context)`}
                  aria-label={contextFileEnabled ? `Exclude ${selectedFile.name} from context` : `Include ${selectedFile.name} in context`}
                >
                  <Icon
                    name={contextFileEnabled ? 'file' : undefined}
                    variant={contextFileEnabled ? 'normal' : 'disabled'}
                    size="small"
                    svg={!contextFileEnabled ? (
                      <svg viewBox="0 0 16 16" focusable="false">
                        <path d="M2 8s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8" cy="8" r="1.5" fill="none" stroke="currentColor"/>
                        <path d="M3 13L13 3" stroke="currentColor" strokeLinecap="round"/>
                      </svg>
                    ) : undefined}
                  />
                  <span className="chat-panel__toolbar-context-file-name">{selectedFile.name}</span>
                </button>
              </>
            )}
            {assetScope && (
              <>
                {(showPack || !!selectedFile) && <div className="chat-panel__toolbar-divider" />}
                <button
                  className="chat-panel__toolbar-asset-scope"
                  onClick={() => setAssetScope(null)}
                  title={`Chat 已绑定到 Asset「${assetScope.assetName}」，点击退出`}
                  aria-label={`Exit Asset KB scope: ${assetScope.assetName}`}
                >
                  <Icon name="gen-ai" size="small" />
                  <span className="chat-panel__toolbar-asset-scope-name">
                    KB: {assetScope.assetName}
                  </span>
                  <Icon name="close" size="small" />
                </button>
              </>
            )}
            {/* UC scope chip — parallel to the asset chip; asset/UC are mutually
                exclusive so at most one renders. Reuses the same toolbar styling. */}
            {ucScope && (
              <>
                {(showPack || !!selectedFile || !!assetScope) && <div className="chat-panel__toolbar-divider" />}
                <button
                  className="chat-panel__toolbar-asset-scope"
                  onClick={() => setUcScope(null)}
                  title={`Chat 已绑定到 Use Case「${ucScope.ucName}」，点击退出`}
                  aria-label={`Exit Use Case KB scope: ${ucScope.ucName}`}
                >
                  <Icon name="gen-ai" size="small" />
                  <span className="chat-panel__toolbar-asset-scope-name">
                    KB: {ucScope.ucName}
                  </span>
                  <Icon name="close" size="small" />
                </button>
              </>
            )}
          </div>
          <ModelSelect
            choices={modelChoice.choices}
            selected={modelChoice.selected}
            onSelect={modelChoice.select}
            disabled={isImpersonating}
          />
          <button
            className="chat-panel__toolbar-attach"
            title={tChat('files.attach')}
            aria-label={tChat('files.attach')}
            onClick={handleAttachClick}
            disabled={isLoading || isUploading || isImpersonating}
          >
            <img src="/icons/icon-chat-upload.svg" alt="" />
            <span>{isUploading ? tChat('files.uploading') : tChat('files.attachLabel')}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept={ALLOWED_UPLOAD_ACCEPT}
          />
        </div>

      </div>
    </aside>
    </OpenWorkspaceFileContext.Provider>
  );
}
